/**
 * Posteingang — kanalübergreifender Mail-Client (E-Mail + WhatsApp), 2-Spalten-Ansicht.
 *
 * Phase 1 (Grundgerüst): Spalte 1 zeigt zugeordnete Vorgänge (`assigned`) UND unzugeordnete
 * Clearing-House-Gruppen (`unassigned`) aus GET /api/inbox in einer gemeinsamen, nach
 * Aktualität sortierten Liste.
 * Phase 2: Spalte 2, Zustand A — Clearing-House-Ansicht für eine unzugeordnete Nachricht
 * (Lesen, Kandidaten-Auswahl, Zuordnen, neuen Vorgang anlegen). Zustand B (Timeline für
 * zugeordnete Vorgänge) + Composer folgen in Phase 3/4.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApi, apiMutate, apiMutateMitZusatz } from "../hooks/useApi";
import type { ApiError } from "../lib/api";
import { Icon, EmptyState, LoadingRows, ErrorNote, StatusPill, Button, Input, Drawer, ListenReiter, SortierMenue, Select, useToast, DateInput, Radio, Textarea, ButtonLink } from "../ui/components";
import { useUrlZustand } from "../hooks/useUrlZustand";
import { oeffneGlobaleSuche } from "../lib/globale-suche";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { anlassAnzeige, attachmentIcon, eventStatusMeta, formatBytes, inquirySourceMeta, money } from "../ui/format";
import { vorgangPfad } from "../lib/pfade";
import type { StatusMeta } from "../ui/format";
import type { AttachmentManifestEntry, Member, Task } from "../ui/types";
import { dekodiereMailBetreff, entferneMailClientArtefakte, mailHtmlFuerRahmen } from "../lib/mail-anzeige";
import { VorgangsPicker, type VorgangsPickerTreffer } from "../ui/VorgangsPicker";
import { SIGNAL_TEXT, SIGNAL_CHIP_TEXT, STUFE_META, type SignalCode, type Stufe } from "../lib/zuordnungs-begruendung";
import { ZuordnungAendernModal, ZuordnungAufhebenModal, ZuordnungsChip } from "../ui/ZuordnungControls";
import { AnfrageAusVormerkungDrawer, type VormerkungsHinweis } from "../ui/AnfrageAusVormerkungDrawer";
import { anlassName } from "../ui/SaisonKampagneDrawer";
import { EingangsbestaetigungDialog, type BestaetigungsEntwurf } from "../ui/EingangsbestaetigungDialog";
type PillTone = StatusMeta["tone"];

interface LatestMessage {
  id: string;
  channel: "email" | "whatsapp";
  subject: string | null;
  snippet: string;
  direction: "inbound" | "outbound";
  receivedAt: string;
}

interface AssignedThread {
  eventId: string;
  occasion: string | null;
  number: string | null;
  status: string;
  eventDate: string | null;
  guestCount: number | null;
  /** UX-Paket 26.08.2026 Punkt 6: fuer die Genesis-Bubble (Ursprungs-Nachricht der Timeline). */
  originChannel: string | null;
  customerNotes: string | null;
  createdAt: string;
  unread: boolean;
  latestMessage: LatestMessage;
}

interface CandidateEvent {
  id: string;
  customerId: string;
  occasion: string | null;
  status: string;
  eventDate: string | null;
  guestCount: number | null;
  updatedAt: string;
  /** WP-B: Vorgangsnummer/Kundenname - Basis der Vorgangskarte im `vorschlag`-Modus und der
   *  Zeilen unter "Kandidaten für diesen Absender" im `auswahl`-Modus. */
  number: string | null;
  customerName: string | null;
  /** WP-B: welche Signale fuer GENAU DIESEN Kandidaten ermittelt wurden - `SIGNAL_TEXT`/
   *  `SIGNAL_CHIP_TEXT` (lib/zuordnungs-begruendung.ts) uebersetzen sie in Betreibersprache. */
  signals: SignalCode[];
}

/** WP-B: die vorbewertete Zuordnungsstufe (`bewerteVorschlag`) fuer eine Gruppe - `eventId`/`stufe`
 *  sind NIE gesetzt, wenn `stufe` "unsicher" waere (kein Vorschlag, Ziel-Interaktion: "nie
 *  automatisch vorausgewaehlt bei 'unsicher'"). Bestimmt, ob `ClearingHousePane` im `vorschlag`-
 *  oder direkt im `auswahl`-Modus startet. */
interface ZuordnungsVorschlag {
  eventId: string | null;
  stufe: Stufe | null;
  signals: SignalCode[];
}

interface UnassignedGroup {
  id: string;
  channel: "email" | "whatsapp";
  fromAddr: string;
  /** Migration 387: die normalisierte Absenderidentitaet (emails.from_addr_norm) - Grundlage der
   *  Gruppierung/des Listen-Keys, damit zwei Anzeigenamen derselben Adresse als EINE Gruppe
   *  zaehlen. `null` nur im Ausnahmefall (from_addr laesst sich nicht normalisieren). */
  fromAddrNorm: string | null;
  subject: string | null;
  snippet: string;
  receivedAt: string;
  messageCount: number;
  unread: boolean;
  /** Baustein B (Posteingang-Verstaendlichkeit): gesetzt, wenn die Gruppe als "keine
   *  Kundenpost" abgelegt wurde (Migration 342). null = normale Unassigned-Gruppe. */
  dismissedAt: string | null;
  candidateEvents: CandidateEvent[];
  vorschlag: ZuordnungsVorschlag;
  /** Punkt 101 Stufe 3: die Absenderadresse ist vorgemerkt und bekam VOR dieser Mail einen
   *  Saison-Vorschlag — nur ein Hinweis, die Anfrage entsteht erst per Klick. */
  vormerkung?: (VormerkungsHinweis & { status: string; versendetAm: string }) | null;
  /** Posteingang S1: Einordnung der Regel-Stufe (Server, lib/posteingang-regeln.ts). null = Kundenpost. */
  regel?: RegelAngabe | null;
  /** Posteingang S1 (P13): SPF/DKIM/DMARC der neuesten Mail. */
  absenderEchtheit?: AbsenderEchtheit;
  /** Posteingang S2 (P6): die KI hat unter mehreren Vorgaengen gewaehlt — dann ist `vorschlag` ihre Wahl. */
  ki?: KiHinweis | null;
}

interface KiHinweis {
  sicherheit: number;
  abstand: number;
  begruendung: string[];
  /** Liegt der Zweitbeste nah dran (Abstand < 0,3), zeigt die Karte ihn mit. */
  alternativeEventId: string | null;
}

const KI_BEGRUENDUNG: Record<string, string> = {
  datum_genannt: "Datum in der Mail genannt",
  uhrzeit_genannt: "Uhrzeit genannt",
  gaeste_genannt: "Gästezahl genannt",
  anlass_genannt: "Anlass genannt",
  bezug_auf_angebot: "bezieht sich auf das Angebot",
  sonstiges: "Inhalt passt",
};

interface RegelAngabe {
  kategorie: "autoantwort" | "unzustellbar" | "eigene" | "plattform_anfrage" | "plattform_status" | "reservierung" | "geschaeftspost" | "werbung";
  label: string;
  grund: string;
  abgelegt: boolean;
  verarbeitbar: boolean;
}

type AbsenderEchtheit = "bestanden" | "fehlgeschlagen" | "unbekannt";

/** Antwort von POST /api/messages/:id/anfrage-vorschlag (Posteingang S1). */
interface AnfrageVorschlag {
  felder: { customerName?: string; company?: string; phone?: string; eventDate?: string; guests?: number; occasion?: string; serviceKind?: "event" | "catering" };
  fundstellen: Partial<Record<string, string>>;
  unsicher: string[];
  fehlend: string[];
  sprache: string | null;
  verdacht: boolean;
  customerEmail: string | null;
  message: string;
  kiStatus: "fertig" | "uebersprungen" | "limit" | "nicht_konfiguriert" | "fehler" | "verdacht";
}

const KI_STATUS_TEXT: Record<AnfrageVorschlag["kiStatus"], string | null> = {
  fertig: "Von MAESTRO aus der Mail vorbefüllt — bitte prüfen.",
  uebersprungen: null,
  limit: "Tageslimit des Assistenten erreicht — bitte selbst ausfüllen.",
  nicht_konfiguriert: null,
  fehler: "Der Assistent war nicht erreichbar — bitte selbst ausfüllen.",
  verdacht: "Die Mail enthält verdächtige Anweisungen — nicht vorbefüllt, bitte selbst prüfen.",
};

interface InboxResponse {
  assigned: AssignedThread[];
  unassigned: UnassignedGroup[];
  /** Baustein B: abgelegte Gruppen — nicht in `unassigned`, aber über den "Abgelegt"-Tab
   *  weiterhin auffindbar/rückholbar (kein spurloses Verschwinden). */
  dismissed: UnassignedGroup[];
}

/** Vereinheitlichte Zeile für die Liste — Zustand (assigned/unassigned) bleibt über `raw` erhalten. */
type ListItem =
  | { key: string; kind: "assigned"; channel: "email" | "whatsapp"; title: string; snippet: string; receivedAt: string; unread: boolean; badge: { label: string; tone: PillTone }; raw: AssignedThread }
  | { key: string; kind: "unassigned"; channel: "email" | "whatsapp"; title: string; snippet: string; receivedAt: string; unread: boolean; badge: { label: string; tone: PillTone }; raw: UnassignedGroup };

const CHANNEL_ICON: Record<"email" | "whatsapp", string> = { email: "mail", whatsapp: "chat" };

/**
 * Posteingang-als-Anfrage (Migration 341): grobe Vorbelegung fuer "Als neue Anfrage anlegen" -
 * `fromAddr` kann bei E-Mail die volle "Name <adresse>"-Kopfzeile tragen (der Ingest speichert
 * das rohe `from`, s. sql/321), bei WhatsApp ist es eine reine Telefonnummer ohne Anzeigenamen.
 * KEINE KI, nur ein simpler Header-Zerleger - der Betreiber korrigiert bei Bedarf im Feld selbst
 * (derselbe Gedanke wie beim Anlass/Datum/Gaeste-Feld unten: grob erkennbar, nicht erzwungen).
 */
function zerlegeAbsender(fromAddr: string): { name: string; adresse: string } {
  const treffer = /^\s*"?([^"<]*?)"?\s*<([^<>]+)>\s*$/.exec(fromAddr);
  if (treffer) {
    const name = treffer[1]?.trim();
    const adresse = treffer[2]!.trim();
    return { name: name && name.length > 0 ? name : adresse, adresse };
  }
  return { name: fromAddr, adresse: fromAddr };
}

function clearingHouseBadge(group: UnassignedGroup): { label: string; tone: PillTone } {
  if (group.dismissedAt) return { label: "Abgelegt", tone: "neutral" };
  // P13: Warnschild vor jeder anderen Einordnung.
  if (group.absenderEchtheit === "fehlgeschlagen") return { label: "Absender nicht bestätigt", tone: "danger" };
  if (group.regel) return { label: group.regel.label, tone: group.regel.kategorie === "plattform_anfrage" ? "info" : "neutral" };
  const n = group.candidateEvents.length;
  if (n === 0 && group.vormerkung) return { label: "Antwort auf Saison-Vorschlag", tone: "info" };
  if (n === 0) return { label: "Neu — kein Kundentreffer", tone: "info" };
  if (n === 1) return { label: "1 Vorgang passt", tone: "warn" };
  return { label: `${n} Vorgänge passen`, tone: "warn" };
}

/**
 * Baustein D (Posteingang-Verstaendlichkeit, 30.08.2026): die Listenzeile zeigt nie mehr rohe
 * Systemplatzhalter wie "(kein Text)". Bevorzugt den (RFC-2047-dekodierten, Baustein C) Betreff,
 * dann die erste sinnvolle Textzeile, und erst im echten Leerfall einen freundlichen Platzhalter.
 */
function listenSnippet(subject: string | null, roherSnippet: string): string {
  const betreff = dekodiereMailBetreff(subject)?.trim() || "";
  const text = entferneMailClientArtefakte(roherSnippet)?.trim() || "";
  if (betreff && text) return `${betreff} — ${text}`;
  return betreff || text || "Ohne Betreff";
}

// `AttachmentManifestEntry`, `formatBytes` und `attachmentIcon` lagen bis 16.09.2026 hier lokal.
// Seit der Dokumente-Reiter der Abrechnung dieselben Mail-Anhang-Metadaten zeigt, stehen sie in
// `ui/types.ts` bzw. `ui/format.ts` — gleiche Schreibweise an beiden Stellen, keine zweite Kopie.

/** Anhang-Karte statt Pill (Screen 36b). ECHTE Bild-Thumbnails sind hier bewusst nicht möglich:
 *  Anhang-BYTES werden für eingehende E-Mail/WhatsApp nie gespeichert, nur die Metadaten
 *  (Migration 317, Kommentar an `emails.attachments_manifest`: "nie der Anhang-Inhalt selbst").
 *  Die Karte zeigt deshalb ein Typ-Icon in einer Kachel statt eines echten Vorschaubilds. */
function AttachmentCard({ a }: { a: AttachmentManifestEntry }) {
  return (
    <div className="flex max-w-[180px] items-center gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-lowest px-2 py-1.5 dark:border-outline/30 dark:bg-white/[0.02]">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-container-high text-secondary dark:bg-white/[0.06] dark:text-secondary-fixed-dim">
        <Icon name={attachmentIcon(a.mimeType)} className="text-[15px]" />
      </span>
      <span className="min-w-0">
        <span className="block truncate font-label-md text-[11px] font-medium text-on-surface-variant">{a.filename ?? "Anhang"}</span>
        <span className="block font-label-md text-[9.5px] text-secondary/80 dark:text-secondary-fixed-dim/80">{formatBytes(a.sizeBytes)}</span>
      </span>
    </div>
  );
}

interface MessageDetail {
  id: string;
  channel: "email" | "whatsapp";
  fromAddr: string;
  toAddr: string | null;
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  rawHeaders: Record<string, string> | null;
  attachmentsManifest: AttachmentManifestEntry[] | null;
  receivedAt: string;
}

function AuthChip({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-label-md text-[10.5px] font-semibold ${ok ? "bg-[#F0EAE0] text-tertiary dark:bg-white/[0.08] dark:text-[#b8ae9e]" : "bg-error-container text-error"}`}>
      <Icon name={ok ? "check_circle" : "cancel"} className="text-[12px]" /> {label}
    </span>
  );
}

/**
 * Spalte 2, Zustand A, vier Modi (Phase C.2 WP-D1 — vorher drei, "liste" ersetzt durch die
 * bewusst zweigeteilten Modi "vorschlag"/"auswahl", s. Ziel-Interaktion im Plan §"Phase C.2"):
 *  - "vorschlag": WP-B hat einen ausreichend starken Kandidaten gefunden (`group.vorschlag.eventId`
 *    gesetzt) - Stufen-Pill, Vorgangskarte, "Warum?"-Begründung, primär "Bestätigen & antworten".
 *  - "auswahl": kein/kein ausreichend starker Vorschlag ODER "Anderen Vorgang wählen" abgelehnt -
 *    Vorgangs-Picker + Kandidatenliste, primär "Zuordnen & antworten" (bzw. "Als neue Anfrage
 *    anlegen" bei 0 Kandidaten).
 *  - "neuerVorgang": Sonderfall - telefonisch längst besprochen, kein Kandidat passt.
 *  - "neueAnfrage": Posteingang-als-Anfrage (Migration 341) - der naheliegendste Weg für eine
 *    inhaltlich neue Kundenanfrage.
 */
type ClearingHouseModus = "vorschlag" | "auswahl" | "neuerVorgang" | "neueAnfrage";

/** Woher die aktuelle `gewaehlt`-Auswahl im `auswahl`-Modus kommt - bestimmt den `reasonCode` von
 *  `POST /api/inbox/assign-group` (Plan: "manuell_gewaehlt" aus der Kandidatenliste, "suche" aus
 *  dem Vorgangs-Picker). */
type AuswahlQuelle = "kandidat" | "suche" | null;

function anzahlText(anzahl: number): string {
  return `${anzahl} Nachricht${anzahl === 1 ? "" : "en"}`;
}

/** Kleines "Neu anlegen"-Dropdown (Anlass/Sonderfall) - dieselbe Verzweigung wie zuvor über zwei
 *  eigene Buttons, jetzt als ein Menü hinter EINEM Einstieg (Plan: "Dropdown „Neu anlegen"
 *  (Anfrage / Vorgang-Sonderfall)"). Design-Ratsche-Vermerk: `Button`/`Card` (#929) sind noch
 *  nicht gemergt (geprüft per `git log origin/main`) - Bestandsklassen/-muster wie im Rest der
 *  Datei, kein neues rohes natives Auswahlfeld. */
function NeuAnlegenMenu({ onAnfrage, onVorgang }: { onAnfrage: () => void; onVorgang: () => void }) {
  const [offen, setOffen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        className="flex items-center gap-1 font-body-sm text-[12.5px] font-semibold text-primary hover:underline"
      >
        <Icon name="add" className="text-[15px]" /> Neu anlegen <Icon name="expand_more" className="text-[14px]" />
      </button>
      {offen && (
        <>
          {/* Klick-außerhalb schließt das Menü - dasselbe simple Overlay-Muster wie sonst im Repo
              (kein Radix-Popover für ein Zwei-Zeilen-Menü). */}
          <div className="fixed inset-0 z-10" onClick={() => setOffen(false)} aria-hidden />
          <div className="absolute bottom-full left-0 z-20 mb-1 w-52 overflow-hidden rounded-lg border border-outline-variant/40 bg-surface-container-lowest shadow-lg dark:border-outline/30 dark:bg-night-card">
            <button
              type="button"
              onClick={() => {
                setOffen(false);
                onAnfrage();
              }}
              className="block w-full px-3 py-2 text-left font-body-sm text-[12.5px] text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-white/[0.05]"
            >
              Als neue Anfrage
            </button>
            <button
              type="button"
              onClick={() => {
                setOffen(false);
                onVorgang();
              }}
              className="block w-full px-3 py-2 text-left font-body-sm text-[12.5px] text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-white/[0.05]"
            >
              Neuer Vorgang (Sonderfall)
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Kompakte Vorgangs-Detailzeile (Anlass, Nummer, Status, Datum, Gäste, Kunde) - Vorgangskarte im
 *  `vorschlag`-Modus. */
function VorgangsKarte({ c }: { c: CandidateEvent }) {
  return (
    <div className="mt-3 rounded-xl border border-outline-variant/40 p-3 dark:border-outline/30">
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate font-body-sm text-[14px] font-semibold text-on-background dark:text-inverse-on-surface">
          {anlassAnzeige(c.occasion) ?? "Ohne Anlass"}
        </span>
        <StatusPill meta={eventStatusMeta(c.status)} />
      </div>
      <p className="mt-1 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
        {[c.number, c.customerName, c.eventDate, c.guestCount ? `${c.guestCount} Gäste` : null].filter(Boolean).join(" · ") || "—"}
      </p>
    </div>
  );
}

/** Punkt 101 Stufe 3 (Mockup b): die erkannte Antwort auf einen Saison-Vorschlag. */
function VormerkungsKarte({ v, primaer, onAnfrage }: { v: NonNullable<UnassignedGroup["vormerkung"]>; primaer: boolean; onAnfrage: () => void }) {
  const zeile = "flex items-center gap-1.5 font-body-sm text-[12.5px] text-on-surface-variant";
  const titel = `${anlassName(v.anlass)} ${v.jahr}`;
  return (
    <div className="mb-3 rounded-xl border border-primary/25 bg-primary/[0.04] p-3" data-testid="vormerkungs-karte">
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="celebration" className="text-[17px] text-primary" />
        <span className="font-body-sm text-[14px] font-semibold text-on-background dark:text-inverse-on-surface">Vormerkung {titel}</span>
        <StatusPill meta={v.status === "aktiv" ? { label: "Aktiv", tone: "success" } : { label: "Abgemeldet", tone: "neutral" }} />
      </div>
      <div className="mt-2 grid gap-1 sm:grid-cols-2">
        <span className={zeile}>
          <Icon name="mail" className="text-[14px]" /> Vorschlag verschickt am {formatDatum(v.versendetAm)}
        </span>
        {v.paketName && (
          <span className={zeile}>
            <Icon name="restaurant_menu" className="text-[14px]" /> {v.paketName}
            {v.preisProPersonCents != null ? ` · ${money(v.preisProPersonCents)} p. P.` : ""}
          </span>
        )}
        <span className={zeile}>
          <Icon name="translate" className="text-[14px]" /> {v.sprache.toLowerCase().startsWith("en") ? "Englisch" : "Deutsch"}
        </span>
      </div>
      <ul className="mt-2 space-y-1">
        <li className={zeile}>
          <Icon name="check" className="text-[13px] text-tertiary" /> Absender ist als Vormerkung für {titel} bekannt
        </li>
        <li className={zeile}>
          <Icon name="check" className="text-[13px] text-tertiary" /> Der {anlassName(v.anlass)}-Vorschlag ging vorher an diese Adresse
        </li>
      </ul>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1 font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">
          <Icon name="info" className="text-[12px]" /> Nur ein Hinweis — die Anfrage entsteht erst mit deinem Klick.
        </p>
        <Button type="button" variant={primaer ? "primary" : "secondary"} className="!py-1.5" onClick={onAnfrage}>
          <Icon name="add_task" className="text-[16px]" /> Daraus Anfrage machen
        </Button>
      </div>
    </div>
  );
}

/** Spalte 2, Zustand A: Clearing-House-Ansicht für eine unzugeordnete Nachricht (Gruppe). */
/** Posteingang S1: Formularfeld mit Fundstelle (wörtliches Zitat aus der Mail) und Hinweisen. */
function Feld({ label, fundstelle, unsicher, fehlt, children }: { label: string; fundstelle?: string; unsicher?: boolean; fehlt?: boolean; children: ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-0.5 flex items-center gap-1.5 font-label-md text-[10.5px] font-semibold uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">
        {label}
        {unsicher && <span className="rounded-full bg-[#F0EAE0] px-1.5 py-px text-[9.5px] normal-case tracking-normal text-[#6F675A]">unsicher</span>}
        {fehlt && !unsicher && <span className="rounded-full bg-surface-container-high px-1.5 py-px text-[9.5px] normal-case tracking-normal text-secondary dark:bg-white/[0.06]">fehlt → Rückfrage</span>}
      </span>
      {children}
      {fundstelle && <span className="mt-0.5 block truncate font-body-sm text-[11px] italic text-secondary dark:text-secondary-fixed-dim">„{fundstelle}“</span>}
    </label>
  );
}

function ClearingHousePane({
  group,
  onZugeordnet,
  onAbgelegt,
}: {
  group: UnassignedGroup;
  /** Anzahl = tatsaechlich bewegte Nachrichten (`assign-group`s `moved`) - die Page-Ebene meldet
   *  damit den Toast NICHT selbst (der braucht den Vorgangsnamen, den nur diese Komponente kennt),
   *  sondern uebernimmt nur Reload + Auswahl + Composer-Fokus. */
  onZugeordnet: (eventId: string, anzahl: number) => void;
  onAbgelegt: () => void;
}) {
  const { data: detail, loading, error } = useApi<MessageDetail>(`/api/messages/${group.id}`);
  const navigate = useNavigate();
  const toast = useToast();
  // 0/1/n Kandidaten + Sicherheits-Stufe entscheidet der Server (bewerteVorschlag, WP-B) - die UI
  // liest nur `group.vorschlag` und startet direkt im passenden Modus (Ziel-Interaktion: "nie
  // automatisch vorausgewaehlt bei 'unsicher'"). `key={group.id}` auf dem Aufrufer (PosteingangPage)
  // sorgt dafuer, dass dieser Anfangswert bei jedem GEWECHSELTEN Absender neu ausgewertet wird.
  const [modus, setModus] = useState<ClearingHouseModus>(group.vorschlag.eventId ? "vorschlag" : "auswahl");
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [gewaehltTitel, setGewaehltTitel] = useState<string | null>(null);
  const [gewaehltQuelle, setGewaehltQuelle] = useState<AuswahlQuelle>(null);
  const [aktionLaeuft, setAktionLaeuft] = useState(false);
  const [aktionFehler, setAktionFehler] = useState<string | null>(null);
  const [ablehnenLaeuft, setAblehnenLaeuft] = useState(false);
  const [neuerAnlass, setNeuerAnlass] = useState("");
  const [neuerName, setNeuerName] = useState("");
  const [ablegenLaeuft, setAblegenLaeuft] = useState(false);
  const [ablegenFehler, setAblegenFehler] = useState<string | null>(null);
  // Posteingang-als-Anfrage: die wichtigsten Anfrage-Felder, Name/Kontakt vorbelegt aus der
  // Nachricht (zerlegeAbsender) - der Rest bleibt bewusst leer (kein KI-Zwang hier, das
  // uebernimmt der bestehende Weckruf-Parse NACH dem Anlegen).
  const [anfrName, setAnfrName] = useState("");
  const [anfrKontakt, setAnfrKontakt] = useState("");
  const [anfrAnlass, setAnfrAnlass] = useState("");
  const [anfrDatum, setAnfrDatum] = useState("");
  const [anfrGaeste, setAnfrGaeste] = useState("");
  const [anfrNachricht, setAnfrNachricht] = useState("");
  // Posteingang S1: dieselben Felder wie das Website-Formular + Vorbefuellung mit Fundstellen.
  const [anfrFirma, setAnfrFirma] = useState("");
  const [anfrTelefon, setAnfrTelefon] = useState("");
  const [anfrArt, setAnfrArt] = useState<"" | "event" | "catering">("");
  const [vorschlag, setVorschlag] = useState<AnfrageVorschlag | null>(null);
  const [vorschlagLaedt, setVorschlagLaedt] = useState(false);
  const [bestaetigung, setBestaetigung] = useState<{ eventId: string; entwurf: BestaetigungsEntwurf } | null>(null);
  const [vormerkungOffen, setVormerkungOffen] = useState(false);
  const istEigene = group.regel?.kategorie === "eigene";
  const istPlattform = group.regel?.kategorie === "plattform_anfrage" || group.regel?.kategorie === "plattform_status";

  const betreff = dekodiereMailBetreff(detail?.subject ?? null);
  const nachrichtentext = entferneMailClientArtefakte(detail?.bodyText ?? null);

  const vorschlagsKarte = group.candidateEvents.find((c) => c.id === group.vorschlag.eventId) ?? null;
  const kiAlternative = group.ki?.alternativeEventId ? (group.candidateEvents.find((c) => c.id === group.ki!.alternativeEventId) ?? null) : null;

  /** Primäraktion im `vorschlag`-Modus: "Bestätigen & antworten" - bewegt die GANZE Gruppe
   *  (Antoines Entscheidung 07.09.2026) auf den vorgeschlagenen Vorgang. */
  async function bestaetigenVorschlag() {
    const eventId = group.vorschlag.eventId;
    if (!eventId) return;
    setAktionLaeuft(true);
    setAktionFehler(null);
    try {
      const result = await apiMutate<{ assigned: boolean; moved?: number }>("/api/inbox/assign-group", "POST", {
        channel: group.channel,
        fromAddr: group.fromAddr,
        eventId,
        reasonCode: "vorschlag_bestaetigt",
        signals: group.vorschlag.signals,
        ...(group.vorschlag.stufe ? { stufe: group.vorschlag.stufe } : {}),
      });
      const anzahl = result.moved ?? 0;
      toast.success(`${anzahlText(anzahl)} dem Vorgang „${anlassAnzeige(vorschlagsKarte?.occasion) ?? "Vorgang"}“ zugeordnet`);
      onZugeordnet(eventId, anzahl);
    } catch (e) {
      setAktionFehler(e instanceof Error ? e.message : "Zuordnung fehlgeschlagen.");
    } finally {
      setAktionLaeuft(false);
    }
  }

  /** Sekundäraktion im `vorschlag`-Modus: "Anderen Vorgang wählen" - Ablehnen. Der Vorschlag
   *  bleibt in der Kandidatenliste sichtbar, ist im `auswahl`-Modus aber NICHT vorausgewählt
   *  (Ziel-Interaktion). `reject_zuordnungs_vorschlag` (WP-C, Migration 388) merkt die Paarung
   *  best-effort - ein Fehlschlag hier blockiert den Moduswechsel nicht (leichtgewichtige
   *  Merk-Notiz, kein Fehlerzustand wert). */
  async function andererVorgang() {
    const eventId = group.vorschlag.eventId;
    if (eventId) {
      setAblehnenLaeuft(true);
      try {
        await apiMutate("/api/inbox/reject-vorschlag", "POST", { channel: group.channel, fromAddr: group.fromAddr, eventId });
      } catch {
        // Bewusst still, s. Kommentar oben.
      } finally {
        setAblehnenLaeuft(false);
      }
    }
    setGewaehlt(null);
    setGewaehltTitel(null);
    setGewaehltQuelle(null);
    setModus("auswahl");
  }

  /** Primäraktion im `auswahl`-Modus: "Zuordnen & antworten" - dieselbe Gruppen-Bewegung wie
   *  `bestaetigenVorschlag`, aber ohne Signale/Stufe (manuelle Wahl statt automatischer Vorschlag)
   *  und mit `reasonCode` je nach Herkunft der Auswahl (Kandidatenliste vs. Vorgangs-Picker). */
  async function zuordnenAuswahl() {
    if (!gewaehlt) return;
    setAktionLaeuft(true);
    setAktionFehler(null);
    try {
      const result = await apiMutate<{ assigned: boolean; moved?: number }>("/api/inbox/assign-group", "POST", {
        channel: group.channel,
        fromAddr: group.fromAddr,
        eventId: gewaehlt,
        reasonCode: gewaehltQuelle === "kandidat" ? "manuell_gewaehlt" : "suche",
      });
      const anzahl = result.moved ?? 0;
      toast.success(`${anzahlText(anzahl)} dem Vorgang „${gewaehltTitel ?? "Vorgang"}“ zugeordnet`);
      onZugeordnet(gewaehlt, anzahl);
    } catch (e) {
      setAktionFehler(e instanceof Error ? e.message : "Zuordnung fehlgeschlagen.");
    } finally {
      setAktionLaeuft(false);
    }
  }

  function kandidatWaehlen(c: CandidateEvent) {
    setGewaehlt(c.id);
    setGewaehltTitel(anlassAnzeige(c.occasion) ?? "Vorgang");
    setGewaehltQuelle("kandidat");
  }

  function suchtrefferWaehlen(t: VorgangsPickerTreffer) {
    setGewaehlt(t.id);
    setGewaehltTitel(anlassAnzeige(t.occasion) ?? "Vorgang");
    setGewaehltQuelle("suche");
  }

  async function neuenVorgangAnlegen() {
    if (!neuerAnlass.trim()) return;
    setAktionLaeuft(true);
    setAktionFehler(null);
    try {
      let customerId = group.candidateEvents[0]?.customerId;
      if (!customerId) {
        const kunde = await apiMutate<{ id: string }>("/api/customers", "POST", {
          name: neuerName.trim() || group.fromAddr,
          ...(group.channel === "email" ? { email: group.fromAddr } : { phone: group.fromAddr }),
        });
        customerId = kunde.id;
      }
      const { data: vorgang, retroMatched } = await apiMutateMitZusatz<{ id: string }, { retroMatched: { linked: number } }>(
        "/api/events",
        "POST",
        { customerId, occasion: neuerAnlass.trim() },
      );
      // Bug 5 (Race, events.ts:611-618): das Retro-Matching in POST /api/events bindet bei
      // Erfolg BEREITS die GANZE Gruppe (retro_match_unassigned_messages vergleicht seit
      // Migration 387 auf die Kundenidentitaet, nicht nur eine einzelne Nachricht) - ein
      // nachfolgender assign-group-Aufruf liefe dann ins Leere (die Gruppe waere schon leer).
      // Nur bei linked === 0 (Ausnahmefall) uebernimmt assign-group die Bewegung, jetzt
      // GRUPPENWEIT statt der fruehere Einzelnachrichten-Weg ueber /assign (Antoines
      // Entscheidung 07.09.2026: Zuordnung bewegt immer den ganzen Absender).
      let anzahl = retroMatched.linked;
      if (retroMatched.linked === 0) {
        const result = await apiMutate<{ assigned: boolean; moved?: number }>("/api/inbox/assign-group", "POST", {
          channel: group.channel,
          fromAddr: group.fromAddr,
          eventId: vorgang.id,
          reasonCode: "neuer_vorgang",
        });
        anzahl = result.moved ?? 0;
      }
      toast.success(`${anzahlText(anzahl)} dem Vorgang „${neuerAnlass.trim()}“ zugeordnet`);
      onZugeordnet(vorgang.id, anzahl);
    } catch (e) {
      setAktionFehler(e instanceof Error ? e.message : "Anlage/Zuordnung fehlgeschlagen.");
    } finally {
      setAktionLaeuft(false);
    }
  }

  /** Oeffnet das Anfrage-Formular und belegt Name/Kontakt aus der Nachricht vor (einmalig - ein
   *  spaeteres erneutes Oeffnen ueberschreibt keine bereits editierten Werte). */
  function neueAnfrageOeffnen() {
    const { name, adresse } = zerlegeAbsender(group.fromAddr);
    setAnfrName(name);
    setAnfrKontakt(adresse);
    setAnfrNachricht(detail?.bodyText ?? "");
    setModus("neueAnfrage");
    if (group.channel === "email" && !vorschlag) void vorbefuellen();
  }

  /** Posteingang S1 (Entscheidung P2): Vorschläge holen, nie selbst anlegen. Überschreibt nur
   *  Felder, die der Betreiber noch nicht verändert hat (leer oder noch die Absender-Vorbelegung). */
  async function vorbefuellen() {
    setVorschlagLaedt(true);
    try {
      const v = await apiMutate<AnfrageVorschlag>(`/api/messages/${group.id}/anfrage-vorschlag`, "POST");
      setVorschlag(v);
      const f = v.felder;
      if (f.customerName) setAnfrName(f.customerName);
      if (v.customerEmail) setAnfrKontakt(v.customerEmail);
      if (f.company) setAnfrFirma(f.company);
      if (f.phone) setAnfrTelefon(f.phone);
      if (f.occasion) setAnfrAnlass(f.occasion);
      if (f.eventDate) setAnfrDatum(f.eventDate);
      if (f.guests) setAnfrGaeste(String(f.guests));
      if (f.serviceKind) setAnfrArt(f.serviceKind);
      if (v.message) setAnfrNachricht(v.message);
    } catch {
      // Ohne Vorschlag bleibt das Formular wie bisher von Hand ausfüllbar.
    } finally {
      setVorschlagLaedt(false);
    }
  }

  async function alsAnfrageAnlegen() {
    if (!anfrName.trim()) return;
    setAktionLaeuft(true);
    setAktionFehler(null);
    try {
      const body: Record<string, unknown> = { customerName: anfrName.trim() };
      if (group.channel === "email") {
        if (anfrKontakt.trim()) body.customerEmail = anfrKontakt.trim();
      } else if (anfrKontakt.trim()) {
        body.phone = anfrKontakt.trim();
      }
      if (anfrAnlass.trim()) body.occasion = anfrAnlass.trim();
      if (anfrDatum) body.eventDate = new Date(anfrDatum).toISOString();
      if (anfrGaeste) body.guests = Number(anfrGaeste);
      if (anfrNachricht.trim()) body.message = anfrNachricht.trim();
      if (anfrFirma.trim()) body.company = anfrFirma.trim();
      if (group.channel === "email" && anfrTelefon.trim()) body.phone = anfrTelefon.trim();
      if (anfrArt) body.serviceKind = anfrArt;
      if (vorschlag?.sprache) body.sprache = vorschlag.sprache;
      // P2: die gesehenen Vorschläge, nur für die Messung „unverändert übernommen“.
      if (vorschlag) {
        const gesehen: Record<string, string | number> = {};
        for (const [k, w] of Object.entries(vorschlag.felder)) if (w !== undefined && w !== null && w !== "") gesehen[k] = w;
        body.vorschlag = gesehen;
        // Vage Angaben, die unverändert übernommen wurden, fragt die Bestätigung nach.
        const unveraendertUnsicher = vorschlag.unsicher.filter(
          (k) => (k === "guests" && anfrGaeste === String(vorschlag.felder.guests ?? "")) || (k === "eventDate" && anfrDatum === (vorschlag.felder.eventDate ?? "")),
        );
        if (unveraendertUnsicher.length > 0) body.unsicher = unveraendertUnsicher;
      }
      // F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md AP3): die Route liefert additiv
      // `eventId` (derselbe sofort angelegte Vorgang) — direkter Sprung zur einen Vorgangsseite,
      // kein Umweg mehr ueber den `/anfragen/:id`-Resolver.
      const result = await apiMutate<{ inquiry: { id: string }; eventId: string; bestaetigung: BestaetigungsEntwurf | null }>(
        `/api/messages/${group.id}/create-inquiry`,
        "POST",
        body,
      );
      // P1: gibt es einen Bestätigungsentwurf, fragt der Dialog — erst danach geht es zum Vorgang.
      if (result.bestaetigung) setBestaetigung({ eventId: result.eventId, entwurf: result.bestaetigung });
      // Nach Erfolg: Verweis auf den neuen Vorgang statt eines stillen Reloads - die
      // Weckruf-Komposition laeuft im Hintergrund, der Betreiber sieht sie dort ankommen.
      else navigate(vorgangPfad(result.eventId));
    } catch (e) {
      setAktionFehler(e instanceof Error ? e.message : "Anfrage-Anlage fehlgeschlagen.");
    } finally {
      setAktionLaeuft(false);
    }
  }

  /** Baustein B: "Ablegen — keine Kundenpost" (dismissed=true) bzw. "Wiederherstellen"
   *  (dismissed=false) — Gruppen-Scope (channel+fromAddr), s. Migration 342. */
  async function ablegen(dismissed: boolean) {
    setAblegenLaeuft(true);
    setAblegenFehler(null);
    try {
      await apiMutate("/api/inbox/dismiss", "POST", { channel: group.channel, fromAddr: group.fromAddr, dismissed });
      onAbgelegt();
    } catch (e) {
      setAblegenFehler(e instanceof Error ? e.message : dismissed ? "Ablegen fehlgeschlagen." : "Wiederherstellen fehlgeschlagen.");
    } finally {
      setAblegenLaeuft(false);
    }
  }

  return (
    // Handy: die ganze Ansicht scrollt als eine Spalte (Mail oben, Aktionen darunter) — ab md wie
    // bisher Mail scrollbar und Aktionsleiste fest unten.
    <div className="flex h-full flex-col overflow-y-auto md:overflow-hidden">
      <div className="shrink-0 p-5 md:flex-1 md:shrink md:overflow-y-auto">
        {loading ? (
          <LoadingRows rows={3} />
        ) : error ? (
          <ErrorNote message={error} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">
              <Icon name={CHANNEL_ICON[group.channel]} className="text-[16px]" />
              <span className="font-medium text-on-background dark:text-inverse-on-surface">{group.fromAddr}</span>
              {group.absenderEchtheit === "bestanden" && <AuthChip label="Absender geprüft" ok />}
              {group.absenderEchtheit === "fehlgeschlagen" && <AuthChip label="Absender nicht bestätigt" ok={false} />}
              {group.channel === "email" && !group.dismissedAt && (
                <span className="rounded-full bg-[#F0EAE0] px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-[#6F675A]">Ohne Zuordnung</span>
              )}
              {group.dismissedAt && (
                <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-secondary dark:bg-white/[0.06] dark:text-secondary-fixed-dim">Abgelegt</span>
              )}
            </div>
            {group.absenderEchtheit === "fehlgeschlagen" && (
              <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-error-container px-3 py-2 font-body-sm text-[12.5px] text-error">
                <Icon name="gpp_maybe" className="mt-0.5 text-[15px]" />
                <span>
                  <strong>Absender nicht bestätigt.</strong> Die Echtheitsprüfung (SPF/DKIM) ist fehlgeschlagen — die Mail kann gefälscht sein. MAESTRO ordnet sie nicht automatisch zu; Angaben vor einer Antwort prüfen.
                </span>
              </p>
            )}
            {group.regel && (
              <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-surface-container-high/60 px-3 py-2 font-body-sm text-[12.5px] text-on-surface-variant dark:bg-white/[0.04]">
                <Icon name={istEigene ? "history" : group.regel.abgelegt ? "inventory_2" : "storefront"} className="mt-0.5 text-[15px]" />
                <span>
                  <strong>{group.regel.abgelegt ? `Automatisch abgelegt · ${group.regel.label}. ` : `${group.regel.label}. `}</strong>
                  {group.regel.grund}
                </span>
              </p>
            )}
            {betreff && <h2 className="mt-3 font-title-md text-[20px] font-bold text-on-background dark:text-inverse-on-surface">{betreff}</h2>}
            {detail?.bodyHtml?.trim() ? (
              <MailRahmen html={detail.bodyHtml} titel={betreff} className="mt-3" />
            ) : (
              <p className="mt-3 whitespace-pre-wrap font-body-sm text-[14px] leading-relaxed text-on-surface-variant">{nachrichtentext || "Ohne Text"}</p>
            )}
            {detail?.attachmentsManifest && detail.attachmentsManifest.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {detail.attachmentsManifest.map((a, i) => (
                  <AttachmentCard key={i} a={a} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t border-outline-variant/30 bg-surface-container-low px-5 py-4 dark:border-outline/20 dark:bg-white/[0.02]">
        {/* Punkt 101 Stufe 3: bei einem laufenden Vorgang nur Zusatzinfo, sonst der Hauptweg. */}
        {group.vormerkung && !group.dismissedAt && modus !== "neueAnfrage" && modus !== "neuerVorgang" && (
          <VormerkungsKarte v={group.vormerkung} primaer={group.candidateEvents.length === 0} onAnfrage={() => setVormerkungOffen(true)} />
        )}
        {vormerkungOffen && group.vormerkung && (
          <AnfrageAusVormerkungDrawer
            vormerkung={group.vormerkung}
            vorbelegung={(() => {
              const { name, adresse } = zerlegeAbsender(group.fromAddr);
              return { name: name === adresse ? "" : name, email: adresse, nachricht: nachrichtentext ?? "" };
            })()}
            messageId={group.id}
            onClose={() => setVormerkungOffen(false)}
          />
        )}
        {bestaetigung && (
          <EingangsbestaetigungDialog
            open
            eventId={bestaetigung.eventId}
            entwurf={bestaetigung.entwurf}
            onFertig={(gesendet) => {
              if (gesendet) toast.success("Eingangsbestätigung gesendet");
              navigate(vorgangPfad(bestaetigung.eventId));
            }}
          />
        )}
        {istEigene ? (
          // Entscheidung P4: Mails eigener Adressen (Altsystem, intern) bleiben sichtbar, werden aber
          // nie verarbeitet — kein Zuordnen, keine Anfrage, kein Abgleich.
          <p className="flex items-center gap-1.5 font-body-sm text-[12.5px] font-medium text-secondary dark:text-secondary-fixed-dim">
            <Icon name="block" className="text-[15px]" /> Wird nicht verarbeitet — nur zur Ansicht.
          </p>
        ) : group.dismissedAt ? (
          <>
            <p className="flex items-center gap-1.5 font-body-sm text-[12.5px] font-medium text-secondary dark:text-secondary-fixed-dim">
              <Icon name="inventory_2" className="text-[15px]" />
              Am {formatDatum(group.dismissedAt)} als „keine Kundenpost“ abgelegt.
            </p>
            <Button type="button" variant="secondary" className="mt-3 !py-1.5" disabled={ablegenLaeuft} onClick={() => void ablegen(false)}>
              {ablegenLaeuft ? "Wird wiederhergestellt…" : "Wiederherstellen"}
            </Button>
            {ablegenFehler && <p className="mt-2 font-body-sm text-[12px] text-error">{ablegenFehler}</p>}
          </>
        ) : modus === "vorschlag" && group.vorschlag.stufe && vorschlagsKarte ? (
          <>
            <div className="flex items-center gap-2">
              <StatusPill meta={STUFE_META[group.vorschlag.stufe]} />
              <span className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                Betrifft {anzahlText(group.messageCount)} dieses Absenders
              </span>
            </div>
            <VorgangsKarte c={vorschlagsKarte} />
            {(group.vorschlag.signals.length > 0 || group.ki) && (
              <div className="mt-3 rounded-xl bg-surface-container-high/50 p-3 dark:bg-white/[0.03]">
                <p className="flex items-center gap-1 font-label-md text-[10.5px] font-semibold uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">
                  <Icon name="help" className="text-[13px]" /> Warum?
                </p>
                <ul className="mt-1.5 space-y-1">
                  {group.ki && (
                    <li className="flex items-start gap-1.5 font-body-sm text-[12.5px] text-on-surface-variant" data-testid="ki-begruendung">
                      <Icon name="auto_awesome" className="mt-0.5 text-[13px] text-primary" />
                      Assistent, {Math.round(group.ki.sicherheit * 100)} % sicher
                      {group.ki.begruendung.length > 0 ? `: ${group.ki.begruendung.map((b) => KI_BEGRUENDUNG[b] ?? b).join(", ")}` : ""}
                    </li>
                  )}
                  {group.vorschlag.signals.map((s) => (
                    <li key={s} className="flex items-start gap-1.5 font-body-sm text-[12.5px] text-on-surface-variant">
                      <Icon name="check" className="mt-0.5 text-[13px] text-tertiary" /> {SIGNAL_TEXT[s]}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {kiAlternative && (
              <div className="mt-3 rounded-xl border border-dashed border-outline-variant/60 p-3 dark:border-outline/40" data-testid="ki-alternative">
                <p className="font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">Knapp dahinter — der Assistent ist sich nicht sicher:</p>
                <p className="mt-1 font-body-sm text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                  {[anlassAnzeige(kiAlternative.occasion) ?? "Ohne Anlass", kiAlternative.number, kiAlternative.eventDate].filter(Boolean).join(" · ")}
                </p>
                <button
                  type="button"
                  className="mt-1 font-body-sm text-[12.5px] font-semibold text-primary hover:underline"
                  onClick={() => {
                    kandidatWaehlen(kiAlternative);
                    setModus("auswahl");
                  }}
                >
                  Diesen stattdessen wählen
                </button>
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="font-body-sm text-[12.5px] font-semibold text-secondary hover:text-on-background dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
                  disabled={aktionLaeuft || ablehnenLaeuft}
                  onClick={() => void andererVorgang()}
                >
                  {ablehnenLaeuft ? "…" : "Anderen Vorgang wählen"}
                </button>
                <NeuAnlegenMenu onAnfrage={neueAnfrageOeffnen} onVorgang={() => setModus("neuerVorgang")} />
              </div>
              <Button type="button" className="!py-1.5" disabled={aktionLaeuft} onClick={() => void bestaetigenVorschlag()}>
                {aktionLaeuft ? "Wird zugeordnet…" : "Bestätigen & antworten"}
              </Button>
            </div>
            {aktionFehler && <p className="mt-2 font-body-sm text-[12px] text-error">{aktionFehler}</p>}
          </>
        ) : modus === "neuerVorgang" ? (
          <div className="space-y-2 rounded-xl border border-outline-variant/40 p-3 dark:border-outline/30">
            {group.candidateEvents.length === 0 && (
              <Input
                type="text"
                value={neuerName}
                onChange={(e) => setNeuerName(e.target.value)}
                placeholder="Name der Kundin/des Kunden"
              />
            )}
            <Input
              type="text"
              value={neuerAnlass}
              onChange={(e) => setNeuerAnlass(e.target.value)}
              placeholder="Anlass des neuen Vorgangs"
            />
            <div className="flex items-center gap-2">
              <Button type="button" className="!py-1.5" disabled={aktionLaeuft || !neuerAnlass.trim()} onClick={() => void neuenVorgangAnlegen()}>
                {aktionLaeuft ? "Wird angelegt…" : "Anlegen & zuordnen"}
              </Button>
              <Button type="button" variant="secondary" className="!py-1.5" onClick={() => setModus(group.vorschlag.eventId ? "vorschlag" : "auswahl")}>
                Abbrechen
              </Button>
            </div>
            {aktionFehler && <p className="mt-2 font-body-sm text-[12px] text-error">{aktionFehler}</p>}
          </div>
        ) : modus === "neueAnfrage" ? (
          <div className="space-y-2 rounded-xl border border-outline-variant/40 p-3 dark:border-outline/30">
            {vorschlagLaedt ? (
              <p className="flex items-center gap-1.5 font-label-md text-[11px] text-secondary dark:text-secondary-fixed-dim">
                <Icon name="auto_awesome" className="text-[13px]" /> MAESTRO liest die Mail …
              </p>
            ) : vorschlag && KI_STATUS_TEXT[vorschlag.kiStatus] ? (
              <p className={`flex items-center gap-1.5 font-label-md text-[11px] ${vorschlag.kiStatus === "verdacht" ? "text-error" : "text-secondary dark:text-secondary-fixed-dim"}`}>
                <Icon name={vorschlag.kiStatus === "fertig" ? "auto_awesome" : "info"} className="text-[13px]" /> {KI_STATUS_TEXT[vorschlag.kiStatus]}
              </p>
            ) : null}
            <Feld label="Name" fundstelle={vorschlag?.fundstellen.customerName}>
              <Input type="text" value={anfrName} onChange={(e) => setAnfrName(e.target.value)} placeholder="Name der Kundin/des Kunden" />
            </Feld>
            <Feld label={group.channel === "email" ? "E-Mail (aus dem Absender)" : "Telefon"}>
              <Input
                type={group.channel === "email" ? "email" : "text"}
                value={anfrKontakt}
                onChange={(e) => setAnfrKontakt(e.target.value)}
                placeholder={group.channel === "email" ? "E-Mail-Adresse" : "Telefonnummer"}
              />
            </Feld>
            <div className="grid gap-2 sm:grid-cols-2">
              <Feld label="Firma" fundstelle={vorschlag?.fundstellen.company}>
                <Input type="text" value={anfrFirma} onChange={(e) => setAnfrFirma(e.target.value)} placeholder="optional" className="min-w-0" />
              </Feld>
              {group.channel === "email" && (
                <Feld label="Telefon" fundstelle={vorschlag?.fundstellen.phone}>
                  <Input type="tel" value={anfrTelefon} onChange={(e) => setAnfrTelefon(e.target.value)} placeholder="optional" className="min-w-0" />
                </Feld>
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Feld label="Anlass" fundstelle={vorschlag?.fundstellen.occasion}>
                <Input type="text" value={anfrAnlass} onChange={(e) => setAnfrAnlass(e.target.value)} placeholder="optional" className="min-w-0" />
              </Feld>
              <Feld label="Art" fundstelle={vorschlag?.fundstellen.serviceKind} fehlt={vorschlag?.fehlend.includes("serviceKind")}>
                <Select
                  ariaLabel="Art der Anfrage"
                  value={anfrArt || "offen"}
                  onValueChange={(v) => setAnfrArt(v === "event" || v === "catering" ? v : "")}
                  options={[
                    { value: "offen", label: "offen" },
                    { value: "event", label: "Veranstaltung im Haus" },
                    { value: "catering", label: "Catering" },
                  ]}
                />
              </Feld>
            </div>
            {/* min-w-0 direkt am Input: Grid-/Flex-Items haben sonst einen impliziten min-width-Defekt. */}
            <div className="grid grid-cols-2 gap-2">
              <Feld label="Datum" fundstelle={vorschlag?.fundstellen.eventDate} unsicher={vorschlag?.unsicher.includes("eventDate")} fehlt={vorschlag?.fehlend.includes("eventDate")}>
                <DateInput value={anfrDatum} onValueChange={(v) => setAnfrDatum(v)} className="min-w-0" />
              </Feld>
              <Feld label="Gäste" fundstelle={vorschlag?.fundstellen.guests} unsicher={vorschlag?.unsicher.includes("guests")} fehlt={vorschlag?.fehlend.includes("guests")}>
                <Input type="number" min={1} value={anfrGaeste} onChange={(e) => setAnfrGaeste(e.target.value)} placeholder="optional" className="min-w-0" />
              </Feld>
            </div>
            {/* Design-Komponenten-Migration (p2): `textarea` bewusst NICHT auf `Input` umgestellt —
                `Input` rendert ausschließlich ein natives `<input>`. */}
            <Feld label="Nachricht (wörtlich aus der Mail)">
              <Textarea value={anfrNachricht} onChange={(e) => setAnfrNachricht(e.target.value)} rows={4} placeholder="Anfragetext" className="resize-none" />
            </Feld>
            <div className="flex items-center gap-2">
              <Button type="button" className="!py-1.5" disabled={aktionLaeuft || vorschlagLaedt || !anfrName.trim()} onClick={() => void alsAnfrageAnlegen()}>
                {aktionLaeuft ? "Wird angelegt…" : istPlattform ? "Als Plattform-Anfrage anlegen" : "Anfrage anlegen"}
              </Button>
              <Button type="button" variant="secondary" className="!py-1.5" onClick={() => setModus(group.vorschlag.eventId ? "vorschlag" : "auswahl")}>
                Abbrechen
              </Button>
            </div>
            <p className="flex items-center gap-1 font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">
              <Icon name="auto_awesome" className="text-[12px]" />{" "}
              {istPlattform
                ? "Plattform-Anfrage: keine Eingangsbestätigung — die Antwort läuft über die Plattform."
                : "Danach liegt die Eingangsbestätigung als Entwurf bereit — gesendet wird nur auf Klick."}
            </p>
            {aktionFehler && <p className="mt-2 font-body-sm text-[12px] text-error">{aktionFehler}</p>}
          </div>
        ) : (
          <>
            <p className="font-body-sm text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">Welcher Vorgang passt?</p>

            <div className="mt-3">
              <VorgangsPicker onSelect={suchtrefferWaehlen} ausgeschlossen={group.candidateEvents.map((c) => c.id)} nurAktive />
            </div>

            {group.candidateEvents.length > 0 && (
              <div className="mt-3">
                <p className="font-label-md text-[10.5px] uppercase tracking-wide text-secondary dark:text-secondary-fixed-dim">Kandidaten für diesen Absender</p>
                <ul className="mt-1.5 space-y-2">
                  {group.candidateEvents.map((c) => (
                    <li key={c.id}>
                      <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-outline-variant/40 p-3 hover:bg-surface-container-high dark:border-outline/30 dark:hover:bg-white/[0.03]">
                        <Radio name="kandidat" checked={gewaehlt === c.id} onChange={() => kandidatWaehlen(c)} className="mt-1" />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate font-body-sm text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">{anlassAnzeige(c.occasion) ?? "Ohne Anlass"}</span>
                            <StatusPill meta={eventStatusMeta(c.status)} />
                          </span>
                          <span className="mt-0.5 block font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                            Letzter Kontakt {relativeZeit(c.updatedAt)}
                            {c.eventDate ? ` · ${c.eventDate}` : ""}
                            {c.guestCount ? ` · ${c.guestCount} Gäste` : ""}
                          </span>
                          {c.signals.length > 0 && (
                            <span className="mt-1 flex flex-wrap gap-1">
                              {c.signals.map((s) => (
                                <span
                                  key={s}
                                  className="rounded-full bg-surface-container-high px-1.5 py-0.5 font-label-md text-[9.5px] text-secondary dark:bg-white/[0.06] dark:text-secondary-fixed-dim"
                                >
                                  {SIGNAL_CHIP_TEXT[s]}
                                </span>
                              ))}
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-3">
                {group.vorschlag.eventId && (
                  <button
                    type="button"
                    className="font-body-sm text-[12.5px] font-semibold text-secondary hover:text-on-background dark:text-secondary-fixed-dim dark:hover:text-inverse-on-surface"
                    onClick={() => setModus("vorschlag")}
                  >
                    Zurück zum Vorschlag
                  </button>
                )}
                <NeuAnlegenMenu onAnfrage={neueAnfrageOeffnen} onVorgang={() => setModus("neuerVorgang")} />
              </div>
              {group.candidateEvents.length > 0 ? (
                <Button type="button" className="!py-1.5" disabled={aktionLaeuft || !gewaehlt} onClick={() => void zuordnenAuswahl()}>
                  {aktionLaeuft ? "Wird zugeordnet…" : "Zuordnen & antworten"}
                </Button>
              ) : group.vormerkung ? null : (
                <Button type="button" className="!py-1.5" disabled={aktionLaeuft} onClick={neueAnfrageOeffnen}>
                  {istPlattform ? "Als Plattform-Anfrage anlegen" : "Als neue Anfrage anlegen"}
                </Button>
              )}
            </div>
            {aktionFehler && <p className="mt-2 font-body-sm text-[12px] text-error">{aktionFehler}</p>}
          </>
        )}

        {!group.dismissedAt && (
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              className="flex items-center gap-1 font-body-sm text-[12px] font-semibold text-secondary hover:text-error dark:text-secondary-fixed-dim"
              disabled={ablegenLaeuft}
              onClick={() => void ablegen(true)}
            >
              <Icon name="inventory_2" className="text-[14px]" /> {ablegenLaeuft ? "Wird abgelegt…" : "Ablegen — keine Kundenpost"}
            </button>
            <p className="flex items-center gap-1 font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">
              <Icon name="lock" className="text-[12px]" /> Jede Zuordnung wird protokolliert
            </p>
          </div>
        )}
        {!group.dismissedAt && ablegenFehler && <p className="mt-2 font-body-sm text-[12px] text-error">{ablegenFehler}</p>}
      </div>
    </div>
  );
}

interface ThreadMessage {
  id: string;
  channel: "email" | "whatsapp";
  direction: "inbound" | "outbound";
  fromAddr: string;
  toAddr: string | null;
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  status: string | null;
  isCoexEcho: boolean;
  attachmentsManifest: AttachmentManifestEntry[] | null;
  receivedAt: string;
}

interface ActivityEntry {
  id: string;
  actorLabel: string | null;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
}

/** Der Notiz-Tab legt einen `activity_log`-Eintrag an (kein eigenes Konzept — taucht dadurch
 *  auch in der Vorgangs-Detailseite auf, s. Plan). In der Posteingang-Timeline wird daraus eine
 *  eigene Bubble-Variante; alle anderen Aktionscodes (Statuswechsel, Angebots-/Rechnungs-Events…)
 *  laufen als dezente System-Systemzeile mit (Screen 36b) statt der Timeline vorenthalten zu
 *  bleiben — dieselben Aktionscodes wie event-detail.tsx ACTIVITY_LABEL, hier bewusst als eigene,
 *  schlankere Kopie statt eines Imports quer über zwei Seiten. */
type FeedItem =
  | { kind: "message"; at: string; message: ThreadMessage }
  | { kind: "notiz"; at: string; task: Task; autor: string | null }
  | { kind: "system"; at: string; activity: ActivityEntry }
  | { kind: "genesis"; at: string; originChannel: string | null; text: string };


const SYSTEM_EVENT_LABEL: Record<string, { label: string; icon: string }> = {
  "event.status_changed": { label: "Status geändert", icon: "sync_alt" },
  "offer.sent": { label: "Angebot gesendet", icon: "send" },
  "offer.chosen": { label: "Angebot angenommen", icon: "celebration" },
  "cost_acceptance.requested": { label: "Kostenübernahme angefordert", icon: "draw" },
  "cost_acceptance.signed": { label: "Kostenübernahme signiert", icon: "verified" },
  "invoice.created": { label: "Rechnung erstellt", icon: "receipt_long" },
  "invoice.sent": { label: "Rechnung gesendet", icon: "mail" },
  "invoice.cancelled": { label: "Rechnung storniert (Gutschrift)", icon: "undo" },
  "inquiry.created": { label: "Anfrage eingegangen", icon: "inbox" },
  "offer.restored": { label: "Version als Entwurf wiederhergestellt", icon: "history" },
  "offer.variante_ki_gefuellt": { label: "Leere Variante von MAESTRO gefüllt", icon: "auto_awesome" },
};

/** Dezente, zentrierte Systemzeile für alles außer Notizen (die eigene Bubble). */
function SystemEventLine({ activity }: { activity: ActivityEntry }) {
  const meta = SYSTEM_EVENT_LABEL[activity.action] ?? { label: activity.action, icon: "history" };
  return (
    <div className="flex justify-center">
      <span className="flex items-center gap-1.5 rounded-full bg-surface-container-high/60 px-3 py-1 font-label-md text-[10.5px] text-secondary dark:bg-white/[0.04] dark:text-secondary-fixed-dim">
        <Icon name={meta.icon} className="text-[12px]" />
        {meta.label} · {formatDatum(activity.createdAt)}
      </span>
    </div>
  );
}

function formatDatum(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function absenderLabel(m: ThreadMessage): string {
  if (m.direction === "inbound") return m.fromAddr;
  return m.isCoexEcho ? "Vom Handy" : "über MAESTRO";
}

/** WP-D2: „⋯"-Menü einer eingehenden Bubble — „Zuordnung ändern…"/„Zuordnung aufheben". Dasselbe
 *  einfache Overlay-Muster wie `NeuAnlegenMenu` oben (Klick außerhalb schließt), hier rechtsbündig
 *  über der Bubble statt am Zeilenfuß. */
function BubbleMenu({ onAendern, onAufheben }: { onAendern: () => void; onAufheben: () => void }) {
  const [offen, setOffen] = useState(false);
  return (
    <div className="relative ml-1 shrink-0">
      <button
        type="button"
        aria-label="Zuordnung bearbeiten"
        onClick={() => setOffen((o) => !o)}
        className="rounded-md p-0.5 text-secondary/70 hover:bg-surface-container-high hover:text-on-background dark:text-secondary-fixed-dim/70 dark:hover:bg-white/[0.06]"
      >
        <Icon name="more_vert" className="text-[15px]" />
      </button>
      {offen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOffen(false)} aria-hidden />
          <div className="absolute right-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-lg border border-outline-variant/40 bg-surface-container-lowest shadow-lg dark:border-outline/30 dark:bg-night-card">
            <button
              type="button"
              onClick={() => {
                setOffen(false);
                onAendern();
              }}
              className="block w-full px-3 py-2 text-left font-body-sm text-[12.5px] text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-white/[0.05]"
            >
              Zuordnung ändern…
            </button>
            <button
              type="button"
              onClick={() => {
                setOffen(false);
                onAufheben();
              }}
              className="block w-full px-3 py-2 text-left font-body-sm text-[12.5px] text-error hover:bg-surface-container-high dark:hover:bg-white/[0.05]"
            >
              Zuordnung aufheben
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** HTML-Mail wie im Mailprogramm — dasselbe Muster wie `AuftragAnfrageKarte` (Punkt 99):
 *  Säuberung + CSP in `mailHtmlFuerRahmen`, Sandbox OHNE allow-scripts/allow-same-origin, nur
 *  Popups für Links. Ohne same-origin ist die Höhe nicht messbar -> feste Höhe + Umschalter. */
function MailRahmen({ html, titel, className }: { html: string; titel: string | null; className: string }) {
  const rahmenDoc = useMemo(() => mailHtmlFuerRahmen(html), [html]);
  const [ganz, setGanz] = useState(false);
  return (
    <div className={className}>
      <iframe
        data-testid="posteingang-mail-rahmen"
        title={`E-Mail: ${titel || "Nachricht"}`}
        srcDoc={rahmenDoc}
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        className={`w-full rounded-lg border border-outline-variant/40 bg-white dark:border-outline/30 ${ganz ? "h-[80vh]" : "h-[320px]"}`}
      />
      <button type="button" className="mt-1 font-body-sm text-[11.5px] font-semibold text-primary hover:underline" onClick={() => setGanz((v) => !v)}>
        {ganz ? "Weniger anzeigen" : "Ganze Mail anzeigen"}
      </button>
    </div>
  );
}

/** Eine Nachrichten-Bubble — links (Kunde) oder rechts (Betrieb), Herkunfts-Chip fürs
 *  outbound-Unterscheiden (über MAESTRO / COEX vom Handy). WP-D2: eingehende Bubbles bekommen
 *  zusätzlich das „⋯"-Menü ("Zuordnung ändern…"/"Zuordnung aufheben") und den Zuordnungs-Chip
 *  (Warum-Panel-Drawer) — beides nur fürs Inbound (Plan: "MessageBubble (Inbound) ⋯-Menü"), eine
 *  eigene Antwort des Betriebs wurde nie über das Clearing House zugeordnet. */
function MessageBubble({ m, eventId, onZuordnungGeaendert }: { m: ThreadMessage; eventId: string; onZuordnungGeaendert: () => void }) {
  const istEigenerVersand = m.direction === "outbound";
  const betreff = dekodiereMailBetreff(m.subject);
  const nachrichtentext = entferneMailClientArtefakte(m.bodyText);
  const [aendernOffen, setAendernOffen] = useState(false);
  const [aufhebenOffen, setAufhebenOffen] = useState(false);
  return (
    <div className={`flex ${istEigenerVersand ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 ${istEigenerVersand ? (m.isCoexEcho ? "bg-surface-container-high" : "bg-primary/10") : "border border-outline-variant/40 bg-surface-container-lowest dark:border-outline/30"}`}>
        <div className="flex items-center gap-1.5">
          <Icon name={CHANNEL_ICON[m.channel]} className="text-[13px] text-secondary dark:text-secondary-fixed-dim" />
          <span className="font-label-md text-[11px] font-semibold text-secondary dark:text-secondary-fixed-dim">{absenderLabel(m)}</span>
          {istEigenerVersand && m.isCoexEcho && <Icon name="smartphone" className="text-[12px] text-secondary" />}
          <span className="ml-auto font-label-md text-[10.5px] text-secondary/80 dark:text-secondary-fixed-dim/80">{formatDatum(m.receivedAt)}</span>
          {!istEigenerVersand && <BubbleMenu onAendern={() => setAendernOffen(true)} onAufheben={() => setAufhebenOffen(true)} />}
        </div>
        {betreff && m.direction === "inbound" && <p className="mt-1 font-body-sm text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">{betreff}</p>}
        {/* Nur eingehend: eigener Versand hat immer eine saubere Textfassung, bleibt wie bisher. */}
        {!istEigenerVersand && m.bodyHtml?.trim() ? (
          <MailRahmen html={m.bodyHtml} titel={betreff} className="mt-1" />
        ) : (
          <p className="mt-1 whitespace-pre-wrap font-body-sm text-[13.5px] leading-relaxed text-on-surface-variant">{nachrichtentext || "Ohne Text"}</p>
        )}
        {m.attachmentsManifest && m.attachmentsManifest.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {m.attachmentsManifest.map((a, i) => (
              <AttachmentCard key={i} a={a} />
            ))}
          </div>
        )}
        {m.status && (m.status === "failed" || m.status === "bounced" || m.status === "complained") && (
          <p className="mt-1.5 flex items-center gap-1 font-label-md text-[10.5px] text-error">
            <Icon name="error" className="text-[12px]" /> Zustellung fehlgeschlagen
          </p>
        )}
        {!istEigenerVersand && (
          <div className="mt-2">
            <ZuordnungsChip messageId={m.id} />
          </div>
        )}
      </div>
      {!istEigenerVersand && (
        <>
          <ZuordnungAendernModal open={aendernOffen} onClose={() => setAendernOffen(false)} messageId={m.id} aktuellerEventId={eventId} onErfolg={onZuordnungGeaendert} />
          <ZuordnungAufhebenModal open={aufhebenOffen} onClose={() => setAufhebenOffen(false)} messageId={m.id} onErfolg={onZuordnungGeaendert} />
        </>
      )}
    </div>
  );
}

/** Eine Notiz — mittig, neutral, klar von einer gesendeten Nachricht unterschieden. */
function NoteBubble({ task, autor }: { task: Task; autor: string | null }) {
  // Notizen-Zusammenfuehrung (Antoine, 25.10.2026): die Notiz ist eine `tasks`-Zeile (Karte
  // "Notizen" am Vorgang). Titel = Anfang des Textes, ein laengerer Rest steht in `notes`.
  const text = `${task.title}${task.notes ?? ""}`;
  return (
    <div className="flex justify-center">
      <div className="max-w-[85%] rounded-xl border border-dashed border-outline-variant/50 bg-surface-container-high/60 px-3.5 py-2 dark:border-outline/30 dark:bg-white/[0.03]">
        <div className="flex items-center gap-1.5">
          <Icon name="sticky_note_2" className="text-[13px] text-secondary dark:text-secondary-fixed-dim" />
          <span className="font-label-md text-[11px] font-semibold text-secondary dark:text-secondary-fixed-dim">
            Notiz{autor ? ` · ${autor}` : ""}
          </span>
          <span className="ml-auto font-label-md text-[10.5px] text-secondary/80 dark:text-secondary-fixed-dim/80">{formatDatum(task.createdAt)}</span>
        </div>
        <p className="mt-1 whitespace-pre-wrap font-body-sm text-[13.5px] leading-relaxed text-on-surface-variant">{text || "Ohne Text"}</p>
      </div>
    </div>
  );
}

/**
 * Ursprungs-Nachricht des Vorgangs (UX-Paket 26.08.2026 Punkt 6) — immer die ERSTE Bubble der
 * Timeline, links wie eine eingehende Nachricht (der Kunde/die Anfrage ist die Quelle). Nur für
 * Formular-/Chat-/Telefon-/Manuell-Ursprünge relevant: kam der Vorgang stattdessen über eine
 * zugeordnete E-Mail/WhatsApp-Nachricht zustande, liegt dessen erste Nachricht ohnehin schon
 * reguär in `feed` (kein Duplikat nötig).
 */
function GenesisBubble({ originChannel, text, at }: { originChannel: string | null; text: string; at: string }) {
  // Wiederverwendet dieselbe Kanal-Bedeutung/Icons wie inquiries.tsx (ui/format.ts) - keine
  // zweite, unabhaengige Herkunfts-Zuordnung fuer denselben Wertebereich.
  const meta = inquirySourceMeta(originChannel);
  return (
    <div className="flex justify-start">
      <div className="max-w-[78%] rounded-2xl border border-outline-variant/40 bg-surface-container-lowest px-3.5 py-2.5 dark:border-outline/30">
        <div className="flex items-center gap-1.5">
          <Icon name={meta.icon} className="text-[13px] text-secondary dark:text-secondary-fixed-dim" />
          <span className="font-label-md text-[11px] font-semibold text-secondary dark:text-secondary-fixed-dim">{meta.label}</span>
          <span className="ml-auto font-label-md text-[10.5px] text-secondary/80 dark:text-secondary-fixed-dim/80">{formatDatum(at)}</span>
        </div>
        <p className="mt-1 whitespace-pre-wrap font-body-sm text-[13.5px] leading-relaxed text-on-surface-variant">{text}</p>
      </div>
    </div>
  );
}

type ComposerTab = "whatsapp" | "email" | "notiz";
/** Konzept 28.09.2026 — dieselben drei Modi wie apps/api/src/lib/gespraech-antwort-entwurf.ts
 *  (`UeberarbeitenModus`); hier lokal, da apps/web nicht aus apps/api importiert. */
type UeberarbeitenModus = "ueberarbeiten" | "kuerzen" | "foermlicher";

/** Letzte eingehende WhatsApp-Nachricht des Vorgangs — Basis für das 24h-Service-Fenster
 *  (Meta-Vorgabe: außerhalb dieses Fensters darf nur mit einem freigegebenen Template geantwortet
 *  werden, was hier bewusst nicht gebaut ist — die UI blockiert den Freitext-Versand stattdessen). */
function letzteEingehendeWhatsappAt(messages: ThreadMessage[]): string | null {
  const eingehend = messages.filter((m) => m.channel === "whatsapp" && m.direction === "inbound");
  if (eingehend.length === 0) return null;
  return eingehend.reduce((spaetest, m) => (m.receivedAt > spaetest ? m.receivedAt : spaetest), eingehend[0]!.receivedAt);
}

function istImWhatsappFenster(letzteEingehendeAt: string | null): boolean {
  if (!letzteEingehendeAt) return false;
  return Date.now() - new Date(letzteEingehendeAt).getTime() < 24 * 60 * 60 * 1000;
}

/** Composer am Fuß der Timeline — WhatsApp/E-Mail/Notiz. Sendet direkt (kein Freigabe-Umweg, ein
 *  Klick auf „Senden" IST hier die Freigabe). Die drei KI-Buttons (Konzept 28.09.2026,
 *  POST .../reply/ueberarbeiten) schreiben NUR den aktuell getippten Text um — nie automatisch,
 *  nie an einen anderen Versandweg als den bestehenden „Senden"-Klick. */
function Composer({
  eventId,
  letzteEingehendeWhatsappAt: letzteWa,
  onGesendet,
  autoFocusToken,
}: {
  eventId: string;
  letzteEingehendeWhatsappAt: string | null;
  onGesendet: () => void;
  /** WP-D1: ein sich änderender Wert (nicht bloß `true`/`false`, sonst feuert der Effekt bei
   *  zwei aufeinanderfolgenden Zuordnungen mit demselben Boolean nicht ein zweites Mal) fokussiert
   *  das Textfeld — Page-Ebene erhöht ihn genau dann, wenn eine Zuordnung gerade erfolgreich war
   *  ("Bestätigen & antworten" o.ä.), nicht bei jedem normalen Öffnen eines Vorgangs. */
  autoFocusToken?: number;
}) {
  const [tab, setTab] = useState<ComposerTab>("email");
  const [text, setText] = useState("");
  const [betreff, setBetreff] = useState("");
  const [senden, setSenden] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Konzept 28.09.2026: die drei vormals deaktivierten KI-Buttons. `ueberarbeitenLaeuft` traegt
  // den laufenden Modus (nur EIN Button gleichzeitig aktiv), `vorherigerText` traegt den Text vor
  // der letzten Ueberarbeitung fuer "Rueckgaengig" - nichts wird gesendet, nur das Feld ersetzt.
  const [ueberarbeitenLaeuft, setUeberarbeitenLaeuft] = useState<UeberarbeitenModus | null>(null);
  const [ueberarbeitenFehler, setUeberarbeitenFehler] = useState<string | null>(null);
  const [vorherigerText, setVorherigerText] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const imFenster = istImWhatsappFenster(letzteWa);
  const gesperrt = tab === "whatsapp" && !imFenster;

  useEffect(() => {
    if (autoFocusToken) textareaRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocusToken]);

  async function absenden() {
    if (!text.trim()) return;
    setSenden(true);
    setFehler(null);
    try {
      if (tab === "notiz") {
        await apiMutate(`/api/events/${eventId}/notes`, "POST", { text: text.trim() });
      } else {
        await apiMutate(`/api/events/${eventId}/reply`, "POST", {
          channel: tab,
          text: text.trim(),
          ...(tab === "email" && betreff.trim() ? { subject: betreff.trim() } : {}),
        });
      }
      setText("");
      setBetreff("");
      setVorherigerText(null);
      onGesendet();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Senden fehlgeschlagen.");
    } finally {
      setSenden(false);
    }
  }

  /** Konzept 28.09.2026: schreibt den AKTUELL im Feld stehenden Text um (+ Modus) - ersetzt nur
   *  das Textfeld, sendet nichts, schreibt nichts. */
  async function ueberarbeiten(modus: UeberarbeitenModus) {
    const aktuell = text.trim();
    if (!aktuell || ueberarbeitenLaeuft) return;
    setUeberarbeitenLaeuft(modus);
    setUeberarbeitenFehler(null);
    try {
      const r = await apiMutate<{ text: string }>(`/api/events/${eventId}/reply/ueberarbeiten`, "POST", { text: aktuell, modus });
      setVorherigerText(text);
      setText(r.text);
    } catch (e) {
      const apiErr = e as ApiError;
      setUeberarbeitenFehler(
        apiErr?.code === "daily_cap_reached"
          ? "Tägliches Kontingent für KI-Überarbeitungen ist erreicht."
          : apiErr?.code === "ai_not_configured"
            ? "KI-Überarbeitung ist nicht konfiguriert."
            : e instanceof Error
              ? e.message
              : "Überarbeiten fehlgeschlagen.",
      );
    } finally {
      setUeberarbeitenLaeuft(null);
    }
  }

  function rueckgaengig() {
    if (vorherigerText === null) return;
    setText(vorherigerText);
    setVorherigerText(null);
  }

  return (
    <div className="border-t border-outline-variant/30 bg-surface-container-low px-5 py-4 dark:border-outline/20 dark:bg-white/[0.02]">
      <div className="flex items-center gap-1">
        {([
          { id: "whatsapp" as const, label: "WhatsApp", icon: "chat" },
          { id: "email" as const, label: "E-Mail", icon: "mail" },
          { id: "notiz" as const, label: "Notiz", icon: "sticky_note_2" },
        ]).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1 rounded-lg px-2.5 py-1 font-body-sm text-[12.5px] font-semibold ${
              tab === t.id ? "bg-primary/10 text-primary" : "text-secondary hover:bg-surface-container-high dark:text-secondary-fixed-dim"
            }`}
          >
            <Icon name={t.icon} className="text-[14px]" /> {t.label}
          </button>
        ))}
        {tab === "whatsapp" && (
          <span
            className={`ml-auto flex items-center gap-1 rounded-full px-2 py-0.5 font-label-md text-[10.5px] font-semibold ${
              imFenster ? "bg-[#F0EAE0] text-tertiary dark:bg-white/[0.08] dark:text-[#b8ae9e]" : "bg-error-container text-error"
            }`}
          >
            <Icon name={imFenster ? "schedule" : "lock_clock"} className="text-[12px]" />
            {imFenster ? "24h-Fenster offen" : "24h-Fenster abgelaufen"}
          </span>
        )}
      </div>

      {gesperrt ? (
        <p className="mt-3 flex items-center gap-1.5 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
          <Icon name="info" className="text-[15px]" />
          Kein WhatsApp-Freitext-Versand möglich — die letzte eingehende Nachricht liegt außerhalb des 24h-Service-Fensters (Meta-Vorgabe).
        </p>
      ) : (
        <>
          {tab === "email" && <Input type="text" value={betreff} onChange={(e) => setBetreff(e.target.value)} placeholder="Betreff (optional)" className="mt-3" />}
          {/* Design-Komponenten-Migration (p2): `textarea` bewusst NICHT auf `Input` umgestellt —
              `Input` rendert ausschließlich ein natives `<input>`, s. Ausnahme-Kommentar oben bei
              `anfrNachricht`. */}
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={tab === "notiz" ? "Interne Notiz…" : "Nachricht…"}
            rows={3}
            className="mt-3 resize-none"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              {tab !== "notiz" &&
                (
                  [
                    { modus: "ueberarbeiten" as const, label: "Mit KI überarbeiten" },
                    { modus: "kuerzen" as const, label: "Kürzen" },
                    { modus: "foermlicher" as const, label: "Förmlicher" },
                  ]
                ).map((b) => (
                  <button
                    key={b.modus}
                    type="button"
                    disabled={!text.trim() || ueberarbeitenLaeuft !== null}
                    title={!text.trim() ? "Erst Text eingeben" : undefined}
                    onClick={() => void ueberarbeiten(b.modus)}
                    className="rounded-lg border border-outline-variant/40 px-2 py-1 font-label-md text-[11px] text-secondary transition-colors hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:text-secondary/60 dark:border-outline/30 dark:text-secondary-fixed-dim dark:disabled:text-secondary-fixed-dim/50"
                  >
                    {ueberarbeitenLaeuft === b.modus ? "…" : b.label}
                  </button>
                ))}
              {vorherigerText !== null && (
                <button type="button" onClick={rueckgaengig} className="font-label-md text-[11px] font-semibold text-primary hover:underline">
                  Rückgängig
                </button>
              )}
            </div>
            <Button type="button" className="ml-auto shrink-0 !py-1.5" disabled={senden || ueberarbeitenLaeuft !== null || !text.trim()} onClick={() => void absenden()}>
              {senden ? "Wird gesendet…" : "Senden"}
            </Button>
          </div>
        </>
      )}
      {fehler && <p className="mt-2 font-body-sm text-[12px] text-error">{fehler}</p>}
      {ueberarbeitenFehler && <p className="mt-2 font-body-sm text-[12px] text-error">{ueberarbeitenFehler}</p>}
    </div>
  );
}

/** Spalte 2, Zustand B: Timeline eines zugeordneten Vorgangs (E-Mail + WhatsApp + Notizen +
 *  System-Ereignisse gemischt). Header zeigt Vorgangsname, ID (Vorgangsnummer, sonst gekürzte
 *  UUID — gleiche Konvention wie event-detail.tsx) und Status-Chip (Screen 36b). */
function ThreadPane({
  eventId,
  title,
  status,
  number,
  originChannel,
  customerNotes,
  createdAt,
  autoFocusToken,
}: {
  eventId: string;
  title: string;
  status: string;
  number: string | null;
  originChannel: string | null;
  customerNotes: string | null;
  createdAt: string;
  /** WP-D1: s. `Composer`s `autoFocusToken` — durchgereicht von `PosteingangPage`. */
  autoFocusToken?: number;
}) {
  const { data: messages, loading, error, reload: reloadMessages } = useApi<ThreadMessage[]>(`/api/events/${eventId}/emails`);
  const { data: activity, reload: reloadActivity } = useApi<ActivityEntry[]>(`/api/events/${eventId}/activity`);
  // Notizen-Zusammenfuehrung (Antoine, 25.10.2026): Notizen kommen aus `tasks` (eine Quelle, dieselbe
  // wie die Karte "Notizen" am Vorgang). Der activity_log-Eintrag `inbox.notiz_hinzugefuegt` bleibt
  // Verlauf (Vorgangsdetail) und wird hier NICHT noch einmal als Zeile gezeigt.
  const { data: notizTasks, reload: reloadNotizen } = useApi<Task[]>(`/api/tasks?eventId=${eventId}`);
  const { data: mitglieder } = useApi<Member[]>("/api/members");
  const notizen = useMemo(() => notizTasks ?? [], [notizTasks]);
  const systemEreignisse = useMemo(() => (activity ?? []).filter((a) => a.action !== "inbox.notiz_hinzugefuegt"), [activity]);
  const feed = useMemo<FeedItem[]>(() => {
    const nachrichten: FeedItem[] = (messages ?? []).map((m) => ({ kind: "message", at: m.receivedAt, message: m }));
    const notizItems: FeedItem[] = notizen.map((t) => {
      const m = t.createdBy ? (mitglieder ?? []).find((x) => x.stackUserId === t.createdBy) : undefined;
      return { kind: "notiz", at: t.createdAt, task: t, autor: m?.displayName ?? m?.email ?? null };
    });
    const systemItems: FeedItem[] = systemEreignisse.map((a) => ({ kind: "system", at: a.createdAt, activity: a }));
    // Punkt 6: nur wenn eine Ursprungs-Notiz existiert (Formular/Chat/Telefon/manuell) - kam der
    // Vorgang stattdessen ueber eine zugeordnete E-Mail/WhatsApp-Nachricht zustande, liegt deren
    // erste Zeile ohnehin schon in `nachrichten`, keine zweite Genesis-Bubble noetig.
    //
    // Migration 341 (Posteingang-als-Anfrage): fuer originChannel 'email'/'whatsapp' gilt das
    // jetzt WIRKLICH (vorher setzte nur die manuelle Komposition originChannel, der Weckruf-Pfad
    // liess es NULL - customer_notes trug den Anfragetext aber schon immer). Die verlinkte Mail
    // (emails.inquiry_id -> event_id) erscheint bereits regulaer in `nachrichten` - eine zweite
    // Bubble mit demselben Text waere ein sichtbares Duplikat.
    const zeigtGenesisBubble = customerNotes?.trim() && originChannel !== "email" && originChannel !== "whatsapp";
    const genesisItem: FeedItem[] = zeigtGenesisBubble ? [{ kind: "genesis", at: createdAt, originChannel, text: customerNotes! }] : [];
    return [...genesisItem, ...nachrichten, ...notizItems, ...systemItems].sort((a, b) => (a.at < b.at ? -1 : 1));
  }, [messages, notizen, mitglieder, systemEreignisse, customerNotes, createdAt, originChannel]);
  const nurNachrichten = useMemo(() => (messages ?? []), [messages]);

  function reload() {
    reloadMessages();
    reloadActivity();
    reloadNotizen();
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-outline-variant/30 px-5 py-3 dark:border-outline/20">
        <div className="min-w-0">
          <h2 className="truncate font-title-md text-[16px] font-bold text-on-background dark:text-inverse-on-surface">{title}</h2>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-label-md text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">{number ?? eventId.slice(0, 8)}</span>
            <StatusPill meta={eventStatusMeta(status)} />
          </div>
        </div>
        {/* Design-Komponenten-Migration (p2): natives `<a href>` bewusst NICHT auf `Button`
            umgestellt — `Button` rendert ausschließlich ein `<button>`-Element, ein Tausch wäre
            eine echte Verhaltensänderung (volle Seitennavigation/Öffnen-in-neuem-Tab-Fähigkeit
            eines echten Links vs. reiner Klick-Handler). */}
        <ButtonLink href={vorgangPfad(eventId)} variant="secondary" className="shrink-0 !py-1.5 !px-3 text-[12.5px]">
          Vorgang öffnen <Icon name="open_in_new" className="ml-1 text-[13px]" />
        </ButtonLink>
      </div>
      <div className="flex-1 overflow-y-auto p-5">
        {loading ? (
          <LoadingRows rows={3} />
        ) : error ? (
          <ErrorNote message={error} />
        ) : feed.length === 0 ? (
          <EmptyState icon="forum" title="Noch keine Nachrichten" hint="Für diesen Vorgang liegt noch keine E-Mail/WhatsApp-Nachricht vor." />
        ) : (
          <div className="space-y-3">
            {feed.map((item) =>
              item.kind === "message" ? (
                <MessageBubble key={item.message.id} m={item.message} eventId={eventId} onZuordnungGeaendert={reload} />
              ) : item.kind === "notiz" ? (
                <NoteBubble key={item.task.id} task={item.task} autor={item.autor} />
              ) : item.kind === "system" ? (
                <SystemEventLine key={item.activity.id} activity={item.activity} />
              ) : (
                <GenesisBubble key="genesis" originChannel={item.originChannel} text={item.text} at={item.at} />
              ),
            )}
          </div>
        )}
      </div>
      <Composer eventId={eventId} letzteEingehendeWhatsappAt={letzteEingehendeWhatsappAt(nurNachrichten)} onGesendet={reload} autoFocusToken={autoFocusToken} />
    </div>
  );
}

/** Zwei-Buchstaben-Kürzel fürs Listen-Avatar — dieselbe Konvention/Optik wie `customers.tsx`
 *  (`initials()`), hier als eigene, schlankere Kopie statt eines Imports quer über zwei Seiten
 *  (Muster bereits im Kopfkommentar von `MessageBubble` oben so begründet). */
function initialen(name: string): string {
  const teile = name.split(/\s+/).filter(Boolean).slice(0, 2);
  const out = teile.map((t) => t[0]?.toUpperCase() ?? "").join("");
  return out || "?";
}

function relativeZeit(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `${min} min`;
  const std = Math.round(min / 60);
  if (std < 24) return `${std} Std.`;
  const tage = Math.round(std / 24);
  return `vor ${tage} Tg.`;
}

/** Listenstandard (Brief § 9): keine Spaltenköpfe — Sortierung als Menü. Standard „Neueste“ ist
 *  die Reihenfolge, in der `items` ohnehin entsteht (neueste zuerst). */
const POSTEINGANG_SPALTEN: SortSpalte<ListItem>[] = [
  { key: "eingang", label: "Eingang", type: "date", accessor: (i) => i.receivedAt },
  // 0 = ungelesen, 1 = gelesen; stabil sortiert, innerhalb bleibt „neueste zuerst“.
  { key: "ungelesen", label: "Ungelesen", type: "number", accessor: (i) => (i.unread ? 0 : 1) },
];
const POSTEINGANG_SORTIERUNG = [
  { label: "Neueste", key: null },
  { label: "Älteste", key: "eingang", dir: "asc" },
  { label: "Ungelesen zuerst", key: "ungelesen", dir: "asc" },
] as const;

type PosteingangReiter = "alle" | "unassigned" | "abgelegt";
const POSTEINGANG_REITER: readonly { key: PosteingangReiter; label: string }[] = [
  { key: "alle", label: "Alle" },
  { key: "unassigned", label: "Ohne Vorgang" },
  { key: "abgelegt", label: "Abgelegt" },
];

export default function PosteingangPage() {
  const { data, loading, error, reload } = useApi<InboxResponse>("/api/inbox");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // WP-D1: Deep-Link `?tab=unassigned` (z. B. von der Glocke).
  // Punkt 115 (Paket 3): Reiter und Suche stehen dauerhaft in der Adresse (`useUrlZustand`) —
  // nach „Zurück“ vom Vorgang ist die Ansicht wieder da.
  const [zustand, setzeZustand] = useUrlZustand({ tab: "alle", q: "" });
  const tab: PosteingangReiter = zustand.tab === "unassigned" || zustand.tab === "abgelegt" ? zustand.tab : "alle";
  const setTab = (t: PosteingangReiter) => setzeZustand({ tab: t });
  const suche = zustand.q;
  const [selected, setSelected] = useState<ListItem | null>(null);
  const [ablegenLaeuftFuer, setAblegenLaeuftFuer] = useState<string | null>(null);
  /** j/k-Tastaturnavigation (Zero/Mail-0-Vergleich, 04.09.2026) — rein zusätzlich zur
   *  bestehenden Tab-Fokus-Navigation der Zeilen, kein Ersatz dafür. */
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  // WP-D1: nach "Bestätigen & antworten" o.ä. soll GENAU der frisch zugeordnete Vorgang
  // ausgewaehlt und der Composer fokussiert werden. Ein Ref (nicht State) fuer die Ziel-ID, DAMIT
  // der Auswahl-Effekt unten sie ueber den asynchronen `reload()` hinweg sieht, ohne selbst einen
  // zusaetzlichen Render auszuloesen (dasselbe Muster wie `vorausgewaehltRef` weiter unten).
  const pendingEventIdRef = useRef<string | null>(null);
  // Ein sich AENDERNDER Wert statt eines Booleans, s. `Composer`s `autoFocusToken`-Kommentar.
  const [composerAutoFocusToken, setComposerAutoFocusToken] = useState(0);

  const items = useMemo<ListItem[]>(() => {
    const assigned: ListItem[] = (data?.assigned ?? []).map((t) => {
      const dekodierterBetreff = dekodiereMailBetreff(t.latestMessage.subject)?.trim();
      return {
        key: `event:${t.eventId}`,
        kind: "assigned",
        channel: t.latestMessage.channel,
        title: anlassAnzeige(t.occasion) || dekodierterBetreff || "Ohne Anlass",
        snippet: listenSnippet(t.latestMessage.subject, t.latestMessage.snippet),
        receivedAt: t.latestMessage.receivedAt,
        unread: t.unread,
        badge: eventStatusMeta(t.status),
        raw: t,
      };
    });
    const gruppeZuListItem = (g: UnassignedGroup): ListItem => ({
      // Migration 387: Schluessel jetzt ueber fromAddrNorm statt dem rohen From-Header - sonst
      // wuerden zwei Anzeigenamen derselben Adresse als zwei Listenzeilen mit demselben Inhalt
      // (dieselbe Gruppe, s. inbox.ts DISTINCT ON) um denselben React-Key konkurrieren.
      key: `unassigned:${g.channel}:${g.fromAddrNorm ?? g.fromAddr}`,
      kind: "unassigned",
      channel: g.channel,
      title: g.fromAddr,
      snippet: listenSnippet(g.subject, g.snippet),
      receivedAt: g.receivedAt,
      unread: g.unread,
      badge: clearingHouseBadge(g),
      raw: g,
    });
    const unassigned: ListItem[] = (data?.unassigned ?? []).map(gruppeZuListItem);
    const dismissed: ListItem[] = (data?.dismissed ?? []).map(gruppeZuListItem);
    return [...assigned, ...unassigned, ...dismissed].sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1));
  }, [data]);

  // "Kunde anschreiben" (customer-detail.tsx) verlinkt hierher mit ?eventId=... - einmalig
  // vorauswaehlen, sobald der passende Eintrag geladen ist. Ein Ref statt State verhindert, dass
  // eine spaetere manuelle Auswahl des Nutzers durch einen erneuten Reload wieder ueberschrieben
  // wird (der Effekt darf nur EINMAL pro Seitenaufruf zuschlagen).
  const vorausgewaehltRef = useRef(false);
  useEffect(() => {
    const eventId = params.get("eventId");
    if (!eventId || vorausgewaehltRef.current) return;
    const treffer = items.find((i) => i.key === `event:${eventId}`);
    if (treffer) {
      vorausgewaehltRef.current = true;
      setSelected(treffer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // WP-D1: Gegenstueck zu `pendingEventIdRef` oben - sobald `reload()` nach einer Zuordnung neue
  // `items` liefert, wird GENAU der frisch zugeordnete Vorgang ausgewaehlt (Zustand B, ThreadPane
  // mit fokussiertem Composer). Fallback (Plan: "Fallback navigate(...)"): taucht der Vorgang
  // (noch) nicht auf - z.B. eine ungewoehnlich langsame Server-Antwort -, hangt sich dieser Pfad
  // an denselben bewaehrten `?eventId=`-Deep-Link-Mechanismus oben (der Guard wird dafuer bewusst
  // zurueckgesetzt, sonst greift er nach dem allerersten Aufruf nie wieder).
  useEffect(() => {
    const pendingId = pendingEventIdRef.current;
    if (!pendingId || loading) return;
    const treffer = items.find((i) => i.key === `event:${pendingId}`);
    if (treffer) {
      setSelected(treffer);
      pendingEventIdRef.current = null;
    } else {
      pendingEventIdRef.current = null;
      vorausgewaehltRef.current = false;
      navigate(`/posteingang?eventId=${pendingId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loading]);

  // Posteingang S1: auch von der Regel-Stufe automatisch abgelegte Gruppen (Newsletter, Plattform-Meldung …).
  const istAbgelegt = (item: ListItem): boolean => item.kind === "unassigned" && (item.raw.dismissedAt != null || item.raw.regel?.abgelegt === true);
  const imReiter = (item: ListItem, r: PosteingangReiter): boolean =>
    r === "unassigned" ? item.kind === "unassigned" && !istAbgelegt(item) : r === "abgelegt" ? istAbgelegt(item) : !istAbgelegt(item);
  // Punkt 115 (G1): die Suche (Absender/Anlass, Betreff/Vorschau) wirkt VOR den Reitern — die
  // Reiter zählen die Suchtreffer.
  const q = suche.trim().toLowerCase();
  const suchTreffer = q ? items.filter((i) => `${i.title} ${i.snippet}`.toLowerCase().includes(q)) : items;
  const reiterAnzahl = (r: PosteingangReiter) => suchTreffer.filter((i) => imReiter(i, r)).length;
  const sort = useListSort("posteingang", POSTEINGANG_SPALTEN);
  const sichtbar = sort.sortiere(suchTreffer.filter((i) => imReiter(i, tab)));

  // Tab-Wechsel wechselt die sichtbare Liste komplett — Markierung geht auf den Anfang zurück,
  // statt auf einen Index zu zeigen, der in der neuen Liste etwas anderes bedeutet.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [tab]);

  // Schrumpft die Liste (z.B. nach "Ablegen"), bleibt der Index sonst außerhalb der Reichweite
  // stehen und die Markierung verschwindet spurlos, bis die nächste Pfeiltaste ihn zurechtrückt.
  useEffect(() => {
    setHighlightedIndex((i) => Math.min(i, Math.max(sichtbar.length - 1, 0)));
  }, [sichtbar.length]);

  // j/k-Tastaturnavigation (Zero/Mail-0-Vergleich, 04.09.2026) — global statt an eine einzelne
  // Zeile gebunden, DAMIT sie unabhängig vom aktuellen DOM-Fokus funktioniert. Ignoriert Tasten,
  // solange ein Eingabefeld (Composer-Textarea, Clearing-House-Formularfelder) fokussiert ist,
  // sonst würde man beim Tippen versehentlich durch die Liste springen.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const ziel = e.target as HTMLElement | null;
      const tag = ziel?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || ziel?.isContentEditable) return;
      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, Math.max(sichtbar.length - 1, 0)));
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        const treffer = sichtbar[highlightedIndex];
        if (treffer) {
          setSelected(treffer);
          void markRead(treffer);
        }
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sichtbar, highlightedIndex]);

  async function markRead(item: ListItem) {
    if (!item.unread) return;
    const body = item.kind === "assigned" ? { eventId: item.raw.eventId } : { channel: item.raw.channel, fromAddr: item.raw.fromAddr };
    try {
      await apiMutate("/api/inbox/mark-read", "POST", body);
      reload();
    } catch {
      // Bewusst still — Ungelesen-Status ist Komfort, kein Fehlerzustand wert.
    }
  }

  /** Baustein B: Schnellzugriff direkt aus der Liste (ohne die Nachricht erst zu öffnen) —
   *  dieselbe Aktion wie der Knopf in der Detailansicht (Migration 342). */
  async function setzeAbgelegt(item: ListItem & { kind: "unassigned" }, dismissed: boolean) {
    setAblegenLaeuftFuer(item.key);
    try {
      await apiMutate("/api/inbox/dismiss", "POST", { channel: item.raw.channel, fromAddr: item.raw.fromAddr, dismissed });
      if (selected?.key === item.key) setSelected(null);
      reload();
    } catch {
      // Bewusst still — dieselbe Aktion steht in der Detailansicht mit sichtbarer Fehlermeldung
      // zur Verfügung, ein Fehlschlag hier lässt die Zeile einfach unverändert stehen.
    } finally {
      setAblegenLaeuftFuer(null);
    }
  }

  return (
    <div className="flex h-[calc(100dvh-58px-4.5rem)] gap-4 lg:h-[calc(100dvh-58px-4rem)]">
      {/* Posteingang S1 (Handy): Liste und Detail im Wechsel — unter md ist immer nur eine Spalte
          sichtbar, ab md wie bisher beide nebeneinander. */}
      <aside className={`${selected ? "hidden md:flex" : "flex"} w-full shrink-0 flex-col overflow-hidden rounded-2xl md:max-w-[360px] border border-outline-variant/40 bg-surface-container-lowest dark:border-outline/30 dark:bg-night-card`}>
        <ListenReiter
          label="Posteingang"
          reiter={POSTEINGANG_REITER.map((r) => ({ ...r, anzahl: reiterAnzahl(r.key) }))}
          aktiv={tab}
          onWahl={setTab}
        />
        {/* Listenstandard (Brief § 9): Sortiermenü statt Spaltenköpfen, gemerkt in URL/localStorage. */}
        <div className="flex items-center justify-end gap-2 border-b border-outline-variant/30 px-3 py-2 dark:border-outline/20">
          <Input
            type="search"
            value={suche}
            onChange={(e) => setzeZustand({ q: e.target.value })}
            placeholder="Suchen …"
            aria-label="Posteingang durchsuchen"
            className="min-w-0 flex-1 !py-1.5"
          />
          <SortierMenue optionen={POSTEINGANG_SORTIERUNG} sortKey={sort.sortKey} dir={sort.dir} onWahl={sort.setze} />
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <LoadingRows rows={5} />
          ) : error ? (
            <ErrorNote zentriert title="Nachrichten konnten nicht geladen werden" message={error} onRetry={reload} />
          ) : sichtbar.length === 0 ? (
            <EmptyState
              icon={q ? "search_off" : "mark_email_read"}
              title={q ? `Keine Nachricht passt zu „${suche.trim()}“` : "Nichts zu tun"}
              hint={tab === "abgelegt" && !q ? "Noch nichts abgelegt." : "Keine Nachrichten in dieser Ansicht."}
              andereTreffer={POSTEINGANG_REITER.filter((r) => r.key !== tab && r.key !== "alle").map((r) => ({
                label: r.label,
                anzahl: reiterAnzahl(r.key),
                onClick: () => setTab(r.key),
              }))}
              link={[
                ...(tab !== "alle" && reiterAnzahl("alle") > 0 ? [{ label: "Alle Nachrichten anzeigen", onClick: () => setTab("alle") }] : []),
                ...(q ? [{ label: "In allen Bereichen suchen", onClick: () => oeffneGlobaleSuche(suche.trim()) }] : []),
                ...(q ? [{ label: "Suche aufheben", onClick: () => setzeZustand({ q: "" }) }] : []),
              ]}
            />
          ) : (
            <ul>
              {sichtbar.map((item, idx) => {
                const abgelegt = istAbgelegt(item);
                return (
                  <li key={item.key}>
                    {/* Baustein B: der ganze Zeilen-Klick oeffnet die Nachricht (role="button" statt
                       ein echtes <button>, DAMIT der Ablegen-Knopf unten ein echtes, gueltig
                       verschachteltes <button> sein kann - zwei <button>-Elemente ineinander waeren
                       ungueltiges HTML). Fuer Playwright/Testing-Library aendert sich nichts: die
                       Accessibility-Rolle bleibt "button". */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSelected(item);
                        void markRead(item);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" && e.key !== " ") return;
                        e.preventDefault();
                        setSelected(item);
                        void markRead(item);
                      }}
                      className={`group flex w-full cursor-pointer items-start gap-2.5 border-b border-outline-variant/20 px-3.5 py-3 text-left transition-colors focus-visible:shadow-[inset_3px_0_0_#C2410C] focus-visible:outline-none dark:border-outline/10 ${
                        selected?.key === item.key
                          ? "bg-primary/[0.07]"
                          : idx === highlightedIndex
                            ? "bg-surface-container-high dark:bg-white/[0.03]"
                            : "hover:bg-surface-container-high dark:hover:bg-white/[0.03]"
                      }`}
                    >
                      {/* Avatar (Zero/Mail-0-Vergleich, 04.09.2026) — dieselbe Optik wie
                          customers.tsx' Kunden-Avatar (bg-primary/14, initials()), hier als
                          eigene, schlankere Kopie (initialen()) statt eines Imports quer über
                          zwei Seiten, kleiner dimensioniert für die kompakte Listenzeile. */}
                      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/14 font-label-md text-[10.5px] font-bold text-primary">
                        {initialen(item.title)}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <Icon name={CHANNEL_ICON[item.channel]} className="shrink-0 text-[15px] text-secondary dark:text-secondary-fixed-dim" />
                            <span className={`truncate font-body-sm text-[13.5px] ${item.unread ? "font-bold text-on-background dark:text-inverse-on-surface" : "font-medium text-on-surface-variant"}`}>
                              {item.title}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-1.5 font-label-md text-[11px] text-secondary dark:text-secondary-fixed-dim">
                            {item.unread && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                            {relativeZeit(item.receivedAt)}
                            {item.kind === "unassigned" && (
                              <button
                                type="button"
                                title={abgelegt ? "Wiederherstellen" : "Ablegen — keine Kundenpost"}
                                disabled={ablegenLaeuftFuer === item.key}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void setzeAbgelegt(item as ListItem & { kind: "unassigned" }, !abgelegt);
                                }}
                                className="ml-1 hidden shrink-0 rounded-md p-0.5 text-secondary/70 hover:bg-surface-container-high hover:text-on-background group-hover:inline-flex dark:text-secondary-fixed-dim/70 dark:hover:bg-white/[0.06]"
                              >
                                <Icon name={abgelegt ? "settings_backup_restore" : "inventory_2"} className="text-[14px]" />
                              </button>
                            )}
                          </span>
                        </div>
                        <p className="line-clamp-1 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">{item.snippet || "Ohne Betreff"}</p>
                        <StatusPill meta={item.badge} />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      <section className={`${selected ? "flex" : "hidden md:flex"} min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-outline-variant/40 bg-surface-container-lowest dark:border-outline/30 dark:bg-night-card`}>
        {selected && (
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="flex min-h-[44px] items-center gap-1 border-b border-outline-variant/30 px-3 font-body-sm text-[13.5px] font-semibold text-primary md:hidden dark:border-outline/20"
          >
            <Icon name="chevron_left" className="text-[20px]" /> Posteingang
          </button>
        )}
        {!selected ? (
          <EmptyState icon="forum" title="Wähle eine Nachricht" hint="Links eine Nachricht anklicken, um sie zu lesen." />
        ) : selected.kind === "unassigned" ? (
          <ClearingHousePane
            // `key` erzwingt einen frischen Mount je Absender-Gruppe - ohne ihn wuerde `modus`
            // (initialisiert aus `group.vorschlag.eventId`) beim Wechsel zwischen zwei
            // unzugeordneten Nachrichten OHNE Navigation weg von dieser Sektion stehen bleiben
            // (dieselbe React-Reconciliation-Falle wie ueberall, wo derselbe Komponententyp an
            // derselben Baumposition bleibt).
            key={selected.raw.id}
            group={selected.raw}
            onZugeordnet={(eventId) => {
              setSelected(null);
              pendingEventIdRef.current = eventId;
              setComposerAutoFocusToken((t) => t + 1);
              reload();
            }}
            onAbgelegt={() => {
              setSelected(null);
              reload();
            }}
          />
        ) : (
          <ThreadPane
            eventId={selected.raw.eventId}
            title={selected.title}
            status={selected.raw.status}
            number={selected.raw.number}
            originChannel={selected.raw.originChannel}
            customerNotes={selected.raw.customerNotes}
            createdAt={selected.raw.createdAt}
            autoFocusToken={composerAutoFocusToken}
          />
        )}
      </section>
    </div>
  );
}
